import { Internals } from "@tento-lona/sheets/internal-ui";
import { rollbackRename } from "./rename-rollback.ts";
import { PageLifecycle } from "../page-lifecycle.ts";
import { cellBackendFactoryOf } from "./cell-backend-factory.ts";
import type { Backend as SheetsBackend } from "@tento-lona/sheets";
import { UserId } from "@tento-lona/sheets";
import type { Option, Unsubscribe } from "@tento-lona/sheets";
import { invariant, nonnull } from "@tento-core/assert";
import { type ResponseError, TypedApi } from "@tento-core/typed-api";
import type { Result } from "@tento-core/result";
import { CellId, RowKey, SheetNetworkKey } from "@tento-lona/sheets";
import { withEphemeralSeed } from "./ephemeral-seed.ts";
import { type EphemeralSheets, ephemeralWireSheet, parseEphemeralSheet, specNetworkId } from "./ephemeral-spec.ts";
import { toRawSheet } from "./sheet-parse.ts";
import { Patch } from "@tento-lona/sheets";
import { SheetRegistries } from "@tento-lona/sheets";
import { SheetKey, SheetKeyRegistry } from "./sheet-key.ts";
import { Network } from "../network.ts";
import type { CellTarget } from "../cells/cell-target.ts";
import { RowLocalId } from "@tento-lona/sheets";
import {
  beginFormulaRewrite,
  type FormulaRewrite,
  rereadFields,
  type RowMirror,
  SheetRow,
} from "../rows/sheet-row.ts";
import { SheetRowFaces, type SheetRowReads } from "../rows/sheet-row-reads.ts";
import { RowFieldWrites } from "../rows/row-field-writes.ts";
// The rows-side sheet's internals are private to `@tento/lona/sheets`. The
// SDK wires the ingestion gate, the promote hook and the instrumentation
// observers onto the rows-side sheets it owns through the package's one SDK
// door, `sheetWiring` — never through its stores.
import { sheetReadsOf } from "@tento-lona/sheets/internal-sdk";
import { CellSetCoordinator } from "../cells/sheet-cell-accessor.ts";
import {
  type ClientInstrumentation,
} from "../internal/doors.ts";
import { NaiveDate } from "@tento-chrono";
import { recoverCreatedRow, RowAccessor } from "../rows/rows-accessor.ts";
import {
  rereadSheetMetadata,
  seedSheetList,
  SheetsClient,
  sheetListCache,
} from "../services/sheets/sheets-client.ts";
import { RowsClient } from "../rows/rows-client.ts";
import { CellStoreView } from "../cells/cell-store-view.ts";
import { SheetHost } from "./sheet-host.ts";
import { Actor, CalendarPreferencesChanged } from "../invalidation.ts";
import type { LiveData } from "@tento-core/livedata";
import { Tracing } from "@tento-core/tracing";
import {
  InvalidationSink,
} from "../invalidation.ts";
import {
  SystemRow,
  BUILTIN_SHEET_CATALOG,
  RowNetworkKey,
  type SyncKey,
} from "@tento-lona/sheets";
import { EncodingRegistry, RowsHttpBackend, SyncKeyWatcher } from "@tento-lona/sheets/platform";
import { ephemeralFromCatalog, isCanonicalKey } from "@tento-lona/sheets/platform";
import { type CellValue, Json, RawSheet } from "@tento-lona/sheets";
import type {
  CalendarPreferences,
} from "../preferences/calendar-preferences.ts";
import { builtinAliasFactories } from "../preferences/alias-factories.ts";
import { viewerWeatherLocationOf } from "../preferences/preferences-aliases.ts";
import type { PreferencesStorage } from "../preferences/preferences-storage.ts";
import { PreferencesClient } from "../preferences/preferences-client.ts";
import { ZoneOutcomes } from "./zone-outcomes.ts";
import { seedThemes, ThemesClient } from "../services/themes/themes-client.ts";
import { type WeatherLocations, WeatherLocationsLoader } from "../weather-locations.ts";
import { CalendarsClient, seedCalendars } from "../services/calendars/calendar-client.ts";
import { UserClient } from "../services/user/user-client.ts";
import { TasksClient } from "../services/tasks/tasks-client.ts";
import { type SchedulingLinksClient, schedulingLinksClientFor } from "../services/scheduling-links/scheduling-links-client.ts";
import { ChatsClient } from "../services/chat/chat-agent-client.ts";
import { ChatSessionStore } from "../services/chat/chat-session-store.ts";
import { EventsClient } from "../services/events/events-client.ts";
import { EventsTable } from "../indices/events-table.ts";
import {
  type DebounceExecutor,
  DebounceExecutors,
  type ListenerErrorHandler,
  ListenerErrorHandlers,
  traceListenerErrors,
  type RequestErrorHandler,
  RequestErrorHandlers,
} from "./client-seams.ts";
import { clientTransport, refusedRowUpdateKey, refusedSheetUpdateKey } from "./client-transport.ts";
import { Appearance } from "../events/event-category-item.ts";
import { MemoryStorage } from "../preferences/preferences-storage.ts";
import { DurableHandoffs, DurableRegistries } from "./durable-handoffs.ts";
import { SheetDataSource } from "./sheet-data-source.ts";
import { RowId } from "@tento-lona/sheets";
import { CellCachingBackend, SeededSyncBackend } from "../sync/sync-backend.ts";
import { SdkBackendAdapter } from "../sync/sdk-backend-adapter.ts";
import { unreachableSheetsBackend } from "./unreachable-sheets-backend.ts";
import { SheetRowMeta } from "../rows/sheet-row-meta.ts";
import type {
  Backend,
  CalendarConfigProvider,
  RecomputeScheduler,
  SyncPoll,
  TaskListConfigProvider,
  SheetModelReads,
} from "@tento-lona/sheets";
import type { Sheet } from "@tento-lona/sheets/platform";
import { BuiltinAliasFactory } from "@tento-lona/sheets";
import { SheetModelCache } from "@tento-lona/sheets/internal-sdk";
import { RowCellCache } from "../sync/row-cell-cache.ts";
import { withSeededReservedRows } from "./builtin-seeds.ts";
import { SheetId } from "@tento-lona/sheets";
import {
  RowWriteError,
  SdkRequestError,
  SheetNotFoundError,
  SheetOpenError,
} from "../errors.ts";
import { ClientDisposedError, ClientLifecycle, DisposeFailures, bindUnobservedRefusals } from "./handle-errors.ts";
import { unobservedRefusalsOf } from "./handle-errors.ts";
import { RowType, type RowTypeDescriptor } from "../rows/row-descriptor.ts";
import type { SyncPollEntry } from "../sync/sync-poll-entry.ts";
import { deepFrozen } from "@tento-lona/sheets/internal-sdk";
import { PromotionWrites } from "./promotion/promotion-writes.ts";
import { ephemeralPromoter, type SheetPromoted } from "./promotion/ephemeral-promotion.ts";
export type { SheetPromoted } from "./promotion/ephemeral-promotion.ts";
import { coreOfLease, leaseOver, SheetHandle } from "./sheet-handle.ts";
import { datedAddress, SheetHandleCore, type SheetHandleDeps } from "./sheet-handle-core.ts";
import { OpenSheetCores } from "./open-sheet-cores.ts";
export { SheetHandle } from "./sheet-handle.ts";

/**
 * Read a sheet for `open`, checked: `sheet` is the stored sheet, or `null` on
 * an explicit miss (404, or a 200 `null` body — `status` says which). Any
 * other answer, or no answer, throws `SheetOpenError`. Uses the
 * result-returning `getResult`, never the tolerant `get` (which hands a
 * non-2xx to the request-error delegate and resolves `undefined`, so a 401
 * read as a miss).
 */
async function readSheetForOpen(
  api: TypedApi<Network.Routes>,
  sheetKey: string,
): Promise<{
  readonly sheet: Option<Network.UserSheetWithRows>;
  readonly status: number;
}> {
  let result: Result<Option<Network.UserSheetWithRows>, ResponseError>;
  try {
    result = await api.getResult("/api/sheets/{sheetKey}", {
      path: { sheetKey: SheetNetworkKey.fromWire(sheetKey) },
    });
  } catch (err) {
    throw SdkRequestError.rejected(err, (answer) =>
      new SheetOpenError({ sheetKey: SheetNetworkKey.fromWire(sheetKey), ...SdkRequestError.Answer.fields(answer) }));
  }
  const error = result.err();
  if (error == null) return { sheet: result.ok() ?? null, status: 200 };
  if (error.code === 404) return { sheet: null, status: 404 };
  const verdict = SdkRequestError.answered(error.code);
  throw new SheetOpenError({
    sheetKey: SheetNetworkKey.fromWire(sheetKey),
    failure: verdict,
    reason: error.reason,
    details: error.details,
  });
}

/**
 * Everything a `LonaSdk.Client` is built from; every field is required
 * (`LonaSdk.Client.headlessScript` returns a script's full config).
 */
export type ClientConfig = {
  storage: PreferencesStorage;
  host: SheetHost;
  /**
   * The lookup tables every sheet this client opens resolves its rows
   * through — row-type specs, computed sources, alias templates, data
   * presets and the render-skip override. `SheetRegistries.BUILTINS`, or
   * `SheetRegistries.extend(SheetRegistries.BUILTINS, overrides)` for a
   * client of its own (another client in the process keeps its own).
   */
  registries: SheetRegistries;
  /**
   * The sink every change this client makes is published into. Rows-side
   * sheets subscribe to it for structural coherence, so it must be a real
   * sink (`InvalidationSink.create()`, or a host's own):
   * `InvalidationSink.NOOP` is a type error here (`InvalidationSink.Live`),
   * and the constructor refuses it for a host that is not type-checked.
   */
  invalidation: InvalidationSink.Live;
  /** Called after an ephemeral sheet is promoted to the server; `null` when the host does not listen. */
  onSheetPromoted: Option<(event: SheetPromoted) => void>;
  /** API origin; `null` for same-origin requests (the browser app). */
  baseUrl: Option<string>;
  /** Bearer token; `null` when the host authenticates by cookie or runs offline. */
  accessToken: Option<string>;
  /**
   * Per-client transport: every request this client sends — its typed API,
   * its sync polls, and an in-process `debounceExecutor`'s durable writes —
   * goes through it. `null` uses the global `fetch` (looked up at send
   * time). A test passes a recording or scripted `fetch` here instead of
   * replacing `globalThis.fetch` or a client method.
   */
  fetch: Option<typeof fetch>;
  /**
   * The rows-side (`@tento/lona/sheets`) wiring, as a function of the client
   * being built. Called once, during construction, once the client's own
   * seams exist: `client.httpBackend()`, `client.preferences`,
   * `client.calendars` and `client.syncPoll` are usable inside it; read
   * anything else lazily (from the providers' callbacks).
   */
  rowsOptions: (client: LonaSdk.Client) => LonaSdk.Client.RowsOptions;
  /**
   * Receives every failure a tolerant request swallowed (the cell-range
   * reads a sheet renders from read empty on failure), as an
   * `SdkRequestError` with boundary `"request"`, step `"tolerate-failure"`.
   * `RequestErrorHandlers.THROW` makes the tolerant call reject instead (a
   * CLI, a server, an example, a test); `RequestErrorHandlers.TRACE` traces
   * and continues (the browser); a host may pass its own (e.g. redirect on
   * `not_logged_in`). It also receives the refusal of a durable write whose
   * `settled` its caller never read (a writable facade's `writeDurable` /
   * `clearDurable`, `row.updateDurable`, `sheets.updateDurable`, `events.updateDurable`,
   * `themes.setDurable`), as that write's `SdkRequestError` — so an
   * unawaited durable write never leaves an unhandled rejection; a caller
   * that reads `settled` owns it and the handler hears nothing.
   */
  requestErrorHandler: RequestErrorHandler;
  /**
   * Receives what a subscriber of this client threw — a cell or row change
   * listener, a sync-state listener, a store bridge — after the other
   * listeners ran (`ListenerErrorHandler`). Optional: absent is
   * `ListenerErrorHandlers.RETHROW` (the host's uncaught error, next
   * microtask); a CLI or TUI passes `ListenerErrorHandlers.TRACE` or its own.
   */
  listenerErrorHandler?: ListenerErrorHandler;
  /**
   * The host's frame-budget port for computed-row recomputes, one per client
   * and shared by every sheet it opens; absent: all of a recompute runs now.
   */
  recomputeScheduler?: RecomputeScheduler;
  /**
   * What executes this client's durable (debounced) writes AND reports
   * their refusals — one decision, so a reporting executor can never be
   * paired with a deaf source. `DebounceExecutors.browser(bridge)`: the page's
   * service-worker bridge, installed through `bridge.install`
   * (page-singleton: one origin and token per page).
   * `DebounceExecutors.inProcess()`: this process, at once, per client (a
   * CLI, a server-side host, a test). `DebounceExecutors.NONE`: every
   * durable write is refused. (A test that wants silence about durable
   * writes uses `TestDebounceExecutors.acceptAll()` from
   * `@tento/lona/testing`.)
   *
   * The client routes each refusal itself: a durable write still in flight
   * (`events.updateDurable`, `row.updateDurable`, a writable facade's
   * `writeDurable`) takes its change back or re-reads, and its `settled`
   * rejects; a refused `sheets.updateDurable` or row PATCH re-reads the
   * sheet or row; whatever no write claimed reaches
   * `onUnclaimedDurableFailure`.
   */
  debounceExecutor: DebounceExecutor;
  /**
   * How this client's event categories look — the theme their colours
   * resolve in, whether the page shows dark, and the platform that picks
   * their glyphs — read back as `client.events.categories.appearance()`.
   * Required, with no default: the browser app passes its theme store's
   * and dark mode's reads; a headless host (a CLI, a server, a test) names
   * `Appearance.HEADLESS`.
   */
  appearance: Appearance;
};

/**
 * The actor of the invalidations a row re-read emits after a refused
 * `row.updateDurable` — the server's state, not any surface's own write, so
 * no self-echo check matches it.
 */
const ROW_REREAD_ACTOR = new Actor("row-reread");
const VIEWER_WEATHER_ACTOR = new Actor("viewer-weather-location");

/**
 * Read-only view of a client's row registry — the row metadata every open
 * `SheetHandle` renders from — handing out each registered row as its frozen
 * reads face (`SheetRowReads`), never the live row or its wire record.
 * Registering a row is a client verb (`platformOf(client).registerRow`), not
 * a registry write. The view itself is frozen.
 */
export interface RowRegistryReads {
  /** The registered row for `id` as its reads, or `null` (a synchronous registry read). */
  readonly peek: (id: RowLocalId) => Option<SheetRowReads>;
  /** Whether a row is registered under `id`. */
  readonly has: (id: RowLocalId) => boolean;
  /** Every registered `[id, row]` pair, in registration order, each row as its reads. */
  readonly iter: () => IterableIterator<[RowLocalId, SheetRowReads]>;
}

/** `client.txLog`'s frozen forwarder over `log` (see `LonaSdk.Client.TxLogReads`). */
function txLogReadsOf(log: SheetModelCache["txLog"]): LonaSdk.Client.TxLogReads {
  return Object.freeze({
    tau: () => log.tau(),
    transactions: () => Object.freeze([...log.transactions()]),
    counters: () => log.counters(),
  });
}

/**
 * `platformOf(client).canonicalCells`: a frozen face forwarding the canonical
 * cell store's reads and change subscriptions (`CanonicalCellReads`) to
 * `store`, and nothing else — no cast reaches `set` / `clear` / `install`.
 * Built once per client (`LonaSdk.Client`'s platform door caches it).
 */
function canonicalCellReadsOf(store: SheetModelCache["canonicalCells"]): LonaSdk.Client.CanonicalCellReads {
  return Object.freeze<LonaSdk.Client.CanonicalCellReads>({
    get: (key, date, cellId) => store.get(key, date, cellId),
    cells: (key, range) => store.cells(key, range),
    customCells: (key, range) => store.customCells(key, range),
    has: (key) => store.has(key),
    isStale: (key, date, cellId) => store.isStale(key, date, cellId),
    subscribe: (key, handler) => store.subscribe(key, handler),
    subscribeAll: (handler) => store.subscribeAll(handler),
  });
}

/**
 * `client.syncPollState`: a frozen face forwarding a live poll map's reads
 * (`get`, `addListener`, `removeListener`) to `live`, and nothing else — no
 * cast reaches its `update`. Built once per client.
 */
function syncPollReadsOf<T>(live: LiveData.Readable<T>): LiveData.Readable<T> {
  return Object.freeze<LiveData.Readable<T>>({
    get: () => live.get(),
    addListener: (target, cb, trigger) => live.addListener(target, cb, trigger),
    removeListener: (target) => live.removeListener(target),
  });
}

/** The builtin alias factories a client's rows accessor registers for `options`. */
function rowsAliasFactories(
  options: LonaSdk.Client.RowsOptions,
  viewerWeatherLocation: () => Option<string>,
): readonly BuiltinAliasFactory[] {
  const base = builtinAliasFactories({
    calendarConfig: options.calendarConfig,
    taskListConfig: options.taskListConfig,
    ...(options.calendarPrefs != null
      ? { calendarPrefs: options.calendarPrefs, viewerWeatherLocation }
      : {}),
  });
  if (options.calendarPrefs != null) return base;
  return [
    ...base,
    emptyAliasTemplate("alias.secondary-timezones"),
    emptyAliasTemplate("alias.default-weather"),
  ];
}

/** A template that expands to nothing, so sheets referencing it still hydrate. */
function emptyAliasTemplate(templateName: string): BuiltinAliasFactory {
  return {
    templateName,
    template: {
      rule: { kind: "static", children: [] },
      dynamicTail: {
        items: () => [],
        // Unreachable: `items` always returns `[]`.
        as: () => {
          throw new Error(`${templateName}: empty stub has no items`);
        },
      },
    },
  };
}

/** Assigned from `LonaSdk.Client`'s static block; see {@link platformOf}. */
let clientPlatformOf: (client: LonaSdk.Client) => LonaSdk.Client.Platform;

/**
 * The platform door of a handle or a client — the renderer host's seam
 * (`@tento/lona/host`); application code does not use it.
 *
 * - `platformOf(handle)` (see `SheetHandle.Platform`): the ingestion gate a
 *   live gesture parks responses behind, the active/inactive switch, the
 *   rows-side prefetch, the render-skip ledger, and the rows-side sheet's
 *   READS (`SheetModelReads`) a render front-end builds its scene from. One
 *   stable object per handle, so a host may compare it by identity.
 * - `platformOf(client)` (see `LonaSdk.Client.Platform`): a render back-end's
 *   (or a host bootstrap's) seam onto the client's local caches — the
 *   identity registry, the canonical cell store, the row registry's writer,
 *   the local-edit chokepoint, the sheet list's cache writers, and the
 *   rows-side sheets' READS (`openSheet` / `ephemeralSheet` / `peekSheet`).
 *   Everything there changes or reads the LOCAL model with no request and no
 *   verdict; application code writes through the checked verbs instead.
 *
 * Neither door hands out a writer of a rows-side sheet or the transaction
 * log's recorder (`client.txLog` is the log's reads). The full `Sheet` is the
 * workspace-only white-box door `instrumentationOf(handle).sheet`
 * (`@tento-lona/internal-testing`); the SDK itself reaches it through its
 * friend modules.
 *
 * Exported from `@tento/lona/host`, not from `@tento/lona` or
 * `@tento/lona/platform`.
 */
export function platformOf(handle: SheetHandle): SheetHandle.Platform;
export function platformOf(client: LonaSdk.Client): LonaSdk.Client.Platform;
export function platformOf(
  target: SheetHandle | LonaSdk.Client,
): SheetHandle.Platform | LonaSdk.Client.Platform {
  return target instanceof SheetHandle
    ? coreOfLease(target).platformView()
    : clientPlatformOf(target);
}

/** The constructor-registered white-box record of each client; no global callback slot. */
const clientInstrumentation = new Internals<LonaSdk.Client, { readonly view: (client: LonaSdk.Client) => ClientInstrumentation }>("LonaSdk.Client");

/** The workspace-only instrumentation view of a constructed client. */
export function clientInstrumentationOf(client: LonaSdk.Client): ClientInstrumentation {
  return clientInstrumentation.of(client).view(client);
}

/**
 * The SDK's root namespace: `LonaSdk.Client` and the types its configuration,
 * ephemeral sheets and payloads are written in.
 */
export namespace LonaSdk {
  /** A row's identity fields (`EphemeralSheets.RowIdentityFields`). */
  export type RowIdentityFields = EphemeralSheets.RowIdentityFields;

  /** One seeded cell of an ephemeral row (`EphemeralSheets.SeedCell`). */
  export type SeedCell = EphemeralSheets.SeedCell;

  /** An ephemeral row's seed cells (`EphemeralSheets.SeedCellData`). */
  export type SeedCellData = EphemeralSheets.SeedCellData;

  /** An ephemeral sheet's row (`EphemeralSheets.EphemeralRow`). */
  export type EphemeralRow = EphemeralSheets.EphemeralRow;

  /**
   * An ephemeral sheet spec (`EphemeralSheets.EphemeralSheet`). A spec that
   * crossed JSON is read with `EphemeralSheet.parse`, which types its ids.
   */
  export type EphemeralSheet = EphemeralSheets.EphemeralSheet;

  export namespace EphemeralSheet {
    /**
     * A spec read at its JSON boundary (an agent's `showEphemeralSheet`
     * command, a dispatched ux-spec command): the sheet `id` as a
     * `SheetNetworkKey` (verbatim), each row's `id` and `children` as
     * `RowId`s, every field type-checked; a field the JSON leaves out reads
     * `null`. A mismatch (a missing `id` / `type` / row `timeScale`, a field
     * of the wrong type) is a thrown `WireDecodeError`.
     */
    export function parse(raw: Json): EphemeralSheet {
      return parseEphemeralSheet(raw);
    }
  }

  /**
   * How `openEphemeral` opens its spec; every field is required.
   *
   * `factory` selects which system rows surround the user's rows.
   * `SystemRow.Factory.PREVIEW` is header-only: no footer surface, so no
   * "New Row" legend and no ephemeral disclaimer banner. That is what an
   * embedded, read-only sheet wants — both footer affordances promote a sheet
   * the embed has no intention of promoting, and on a short embed they cost
   * more height than the content. `null` for the full editable chrome.
   */
  export type EphemeralOpenOptions = {
    /**
     * A sheet already open for the spec's id: `"reuse"` returns a handle on
     * it; `"exclusive"` refuses with `SheetHandle.InUseError` (the caller
     * needs the sheet `spec` describes, and never tears down one another
     * caller holds).
     */
    readonly onExisting: "reuse" | "exclusive";
    /**
     * `"ephemeral"` for a local sheet an edit promotes; `"template"` for one
     * opened from a template (its claim banner).
     */
    readonly mode: SheetHandle.EphemeralMode;
    /** The system rows around the sheet; `null` for the full editable chrome. */
    readonly factory: Option<SystemRow.Factory>;
  };

  /** A single cell write for an ephemeral sheet. */
  export type EphemeralCellWrite = {
    /** The row's key as the writer names it (its wire spelling: a row id or a lookup key). */
    rowId: RowNetworkKey;
    date: string;
    /** The cell's value, as JSON (an agent's tool output carries it). */
    value: Json;
  };

  export class Client {
    static {
      clientPlatformOf = (client) => client.#platformDoor();
    }

    /**
     * The client's ONE paced sync-status poll (`GET /api/sync/{syncKey}/status`).
     * The rows-side accessor is built over it; every poller keyed on it shares
     * one loop per sync key.
     */
    readonly syncPoll: SyncPoll;
    readonly #sheets: SheetsClient;
    readonly #rows: RowsClient;
    readonly #events: EventsClient;
    readonly #calendars: CalendarsClient;
    readonly #user: UserClient;
    /**
     * This client's sheet keys: every spelling of a sheet
     * (`sheetKeys.make(":my-sheet", null)`, `"ss_…"`, `"[user]:lookup"`)
     * interned to one `SheetKey`, a bare `:lookup` scoped to the user
     * `setCurrentUser` bound. Per client — another client resolves the same
     * spelling against its own user.
     */
    readonly sheetKeys: SheetKeyRegistry;
    readonly #tasks: TasksClient;
    /** One scheduling-links store per user (`schedulingLinksFor`). */
    readonly #schedulingLinksByUser = new Map<string, SchedulingLinksClient>();
    /** Client-scoped cell-change fan-out (local edits, column invalidation). */
    readonly #dataSource: SheetDataSource;
    /** The seams every `SheetHandle` this client opens is parsed against. */
    readonly #deps: SheetHandleDeps;
    /** The typed API every request of this client goes through. */
    readonly #api: TypedApi<Network.Routes>;
    /**
     * The rows-side (`@tento/lona/sheets`) accessor every `SheetHandle` this
     * client opens hydrates its `rowsSheet` through. Built in the constructor
     * from `ClientConfig.rowsOptions`.
     */
    readonly #modelCache: SheetModelCache;
    /** The client's rows backend (the cell-backend factory's `sheets`, HTTP by
     *  default): the one transport of every SDK row write (`RowFieldWrites`). */
    readonly #rowsHttp: SheetsBackend;
    /** Orders checked cell writes across this client's sheet accessors. */
    readonly #cellSetCoordinator = new CellSetCoordinator();
    /** The builtin alias templates `modelCache` registers (reused by preview accessors). */
    readonly #rowAliases: readonly BuiltinAliasFactory[];
    readonly #preferences: PreferencesClient;
    readonly #zones: ZoneOutcomes;
    readonly #chats: ChatsClient;
    readonly #themes: ThemesClient;
    /** This client's weather-location table (`getWeatherLocations`). */
    readonly #weatherLocations: WeatherLocationsLoader;
    /** Whether `#viewerWeatherTable` has started the table's load. */
    #viewerWeatherLoadStarted = false;
    readonly host: SheetHost;
    readonly invalidation: InvalidationSink;
    readonly #onSheetPromoted: Option<(event: SheetPromoted) => void>;
    /** The open sheet cores (opened under their aliases, and parsed) and their release. */
    readonly #cores = new OpenSheetCores({
      rows: () => this.#deps.rows,
      modelCache: () => this.#modelCache,
      disposed: () => this.#lifecycle.disposed,
    });
    /** Every durable-write registry of this client — its own below and
     *  `EventsTable`'s — offered each refusal and failed on `dispose`. */
    readonly #durables = new DurableRegistries();
    /** The durable cell and row writes in flight (`SheetHandleDeps.durableWrites`). */
    readonly #durableWrites = this.#durables.create();
    /** The page this client runs in, watched for its unload
     *  (`SheetHandleDeps.page`); page-less in Deno or a worker. */
    readonly #page = new PageLifecycle("onpagehide" in globalThis ? globalThis : null);
    /** The host's `ClientConfig.requestErrorHandler`. */
    readonly #requestErrorHandler: RequestErrorHandler;
    /** The host's `ClientConfig.listenerErrorHandler` (`ListenerErrorHandlers.RETHROW` when absent). */
    readonly #listenerErrors: ListenerErrorHandler;
    /** `onUnclaimedDurableFailure` subscribers. */
    readonly #unclaimedDurableFailures = new Set<
      (failure: DebounceExecutor.Failure) => void
    >();
    /** This client's `ClientConfig.debounceExecutor` binding and its
     *  refusal subscription; `null` once `dispose` ran. */
    /** Drops this client's persistence-flush hook from its host (`dispose`). */
    #uninstallPersistenceFlush: Option<() => void> = null;
    #debounceBinding: Option<{
      readonly binding: DebounceExecutor.Binding;
      readonly unsubscribe: () => void;
    }> = null;
    /**
     * The one liveness this client shares with every facade it builds and
     * every standalone row it opens (`LonaSdk.Client.Lifecycle`): `dispose`
     * retires it, and every client verb, facade verb and row verb throws
     * afterwards — also through a reference taken before.
     */
    readonly #lifecycle = new ClientLifecycle();
    /**
     * Row-identity stage 2, off by default: a promote sends its draft's
     * client-minted row id (`PendingRowPersist`). Flipped through
     * `clientInstrumentationOf(client).clientRowIds` until it is a host setting.
     */
    #clientRowIds = false;

    static readonly #instrumentation = {
      view: (client: Client): ClientInstrumentation => client.#instrumentationDoor(),
    };

    constructor(config: ClientConfig) {
      clientInstrumentation.register(this, Client.#instrumentation);
      invariant(
        !(InvalidationSink.NOOP_BRAND in config.invalidation),
        "LonaSdk.Client: construct the client with a real `invalidation` sink " +
          "(e.g. `InvalidationSink.create()`); rows-side sheets subscribe to it.",
      );
      // The three seams have no default; a host that is not type-checked
      // hears which one it left out.
      invariant(
        config.requestErrorHandler != null,
        "LonaSdk.Client: `requestErrorHandler` is required — " +
          "`RequestErrorHandlers.THROW`, `RequestErrorHandlers.TRACE` or the " +
          "host's handler",
      );
      invariant(
        config.debounceExecutor != null,
        "LonaSdk.Client: `debounceExecutor` is required — " +
          "`DebounceExecutors.browser(bridge)`, `DebounceExecutors.inProcess()` or " +
          "`DebounceExecutors.NONE`",
      );
      // One API, one sync client and one durable-write binding, all bound to
      // this client's origin, token and `fetch` (`clientTransport`).
      const { baseUrl, binding, api, syncClient } = clientTransport(config, (verb) => this.#assertLive(verb));
      const requestErrorHandler = config.requestErrorHandler;
      this.#requestErrorHandler = requestErrorHandler;
      // One sink for what this client's subscribers throw: every cell accessor,
      // store bridge and fan-out it builds reports there.
      // `TRACE` gets this client's own memory of what it wrote (`traceListenerErrors`).
      const given = config.listenerErrorHandler ?? ListenerErrorHandlers.RETHROW;
      const listenerErrors = given === ListenerErrorHandlers.TRACE ? traceListenerErrors() : given;
      this.#listenerErrors = listenerErrors;
      // A durable write's refusal whose `settled` its caller never read goes
      // to the same handler (`observedDurable`): the client's verbs, its
      // rows' and its sheets' joined rows' watch their `Durable` under its
      // lifecycle.
      bindUnobservedRefusals(this.#lifecycle, requestErrorHandler);
      this.syncPoll = syncClient.poll;
      this.#onSheetPromoted = config.onSheetPromoted;
      const invalidationSink = config.invalidation;
      // THE identity scope for this Client. Minted first so every
      // consumer below — `EventsTable`, `SheetDataSource`, `deps`, `RowsClient`,
      // the rows accessor — is handed the same registry. Two Clients in one
      // process therefore resolve equal wire keys to DISTINCT `RowLocalId`s,
      // and nothing in production reads `RowLocalId.Registry.instance`.
      const idsReg = new RowLocalId.Registry();
      // Client-scoped encoding registry — populated with every
      // built-in wire decoder (event.v1, task.v1, weather.v1,
      // slot.v1). Threaded into RowsHttpBackend so every RowCellCache it
      // produces hydrates cells at the fetch boundary using the
      // server-declared `metadata.encoding` on each row.
      const encodings = EncodingRegistry.withBuiltins();
      // The cell backends, from the factory registered for this config (SDK
      // tests, the in-memory proof store: `withCellBackend`) or today's HTTP
      // composition (`CellBackendFactory.HTTP`): `SyncHttpBackend` and the
      // sheets-side `RowsHttpBackend` over the same API.
      const { legacy: legacyBackend, sheets: sheetsBackend } = cellBackendFactoryOf(config)({
        api,
        syncClient,
        encodings,
        listenerErrors,
      });
      // Wrapped with `SdkBackendAdapter`: the write methods it lists as
      // migrated (`writeCell`, `clearCell`, `deleteRow`, `updateRow`) route
      // through sheets' Backend; the rest delegate to `legacyBackend` (see
      // its residual table in sdk-backend-adapter.ts).
      // `Network.Routes` is a superset of the sheets routes; the parity
      // assertion in `_network-type-check.ts` (`RoutesParity`) keeps it so.
      const backend = new SdkBackendAdapter(
        sheetsBackend,
        legacyBackend,
      );
      const invalidation = config.invalidation;
      this.#api = api;
      this.host = config.host;
      this.invalidation = invalidation;
      this.#preferences = new PreferencesClient(
        config.storage,
        config.host,
        invalidation,
        this.#lifecycle,
      );
      this.#zones = new ZoneOutcomes(this.#preferences.timezone(), this.#lifecycle, listenerErrors);
      this.#calendars = new CalendarsClient(
        api,
        this.#preferences,
        invalidation,
        async () => this.#deps.ids.currentUser ?? (await this.#user.get()).id,
        this.#lifecycle,
      );
      this.#user = new UserClient(api, this.#lifecycle);

      // The rows side, wired from the host's factory now that `httpBackend()`,
      // `preferences`, `calendars` and `syncPoll` exist: built over this
      // client's registry, sink and sync poll, so a rows-side sheet and the
      // client resolve the same `RowLocalId`s and publish into the same sink.
      const rowOptions = config.rowsOptions(this);
      this.#rowAliases = rowsAliasFactories(
        rowOptions,
        viewerWeatherLocationOf(
          () => this.#viewerWeatherTable(),
          () => this.#preferences.timezone().effectiveTimezoneName(),
        ),
      );
      this.#modelCache = new SheetModelCache({
        requestFailureReporter: (error, report) => this.#page.reportUnlessLeaving(error, report),
        backend: rowOptions.backend,
        listenerErrors,
        registry: idsReg,
        invalidationSink: invalidation,
        builtinAliases: this.#rowAliases,
        ...(config.recomputeScheduler != null ? { recomputeScheduler: config.recomputeScheduler } : {}),
        syncPoll: this.syncPoll,
        registries: config.registries,
      });

      backend.attachCellAuthority(cache => {
        const key = cache.rowId.toString();
        if (isCanonicalKey(key)) return CellStoreView.of("canonical", this.#modelCache.canonicalCells, key, () => cache.cardinality, null).retainAuthority();
        const localId = idsReg.resolve(RowKey.of(key));
        const releaseGuard = this.#modelCache.ordinaryCells.guard(localId, () => cache.isEvictable, () => cache.evictCells());
        this.#cacheGuards.add(releaseGuard);
        // The backend dropped the cache (retention eviction, row delete): its guard goes with it.
        cache.onDropped(() => {
          this.#cacheGuards.delete(releaseGuard);
          releaseGuard();
        });
        return CellStoreView.of("local", this.#modelCache.ordinaryCells, localId, () => cache.cardinality, null).retainAuthority();
      });

      const eventsTable = new EventsTable(
        api,
        invalidationSink,
        idsReg,
        (rowKey: RowNetworkKey) => this.#rowHandle(rowKey, null),
        () => this.#modelCache.canonicalCells,
        // Resolves each write's target events-row key from the
        // server-rendered `CalendarDisplay.eventsLookupKey` (provider-correct,
        // no `:~google:` assumption).
        () => this.#calendars,
        this.#durables.create(),
      );
      const indexCtx = {
        api,
        invalidation: invalidationSink,
        encodings,
        rowMeta: (rowKey: RowNetworkKey) => legacyBackend.rowMeta(RowId.of(rowKey)),
        openRowHandle: (rowKey: RowNetworkKey, rowType: Option<string>) =>
          this.#rowHandle(rowKey, rowType),
        eventsTable,
      };
      this.#dataSource = new SheetDataSource(
        invalidationSink,
        idsReg,
        // Registry handle resolver: `applyCellEditLocal` + the
        // canonical store->handle bridge reach handle-side readers
        // with zero open sheets.
        (rowId) => backend.rowHandle(rowId),
        listenerErrors,
        (rowId) => backend.peekRowHandle(rowId),
      );
      this.sheetKeys = new SheetKeyRegistry(() => idsReg.currentUser);
      this.#rowsHttp = sheetsBackend;
      const rowWrites = this.#rowFieldWrites(this.#modelCache);
      this.#deps = {
        ids: idsReg,
        sheetKeys: this.sheetKeys,
        rows: new SheetRow.Registry(config.registries),
        rowFaces: new SheetRowFaces(),
        api,
        cellSetCoordinator: this.#cellSetCoordinator,
        host: config.host,
        invalidation: invalidationSink,
        dataSource: this.#dataSource,
        ownsDataSource: false,
        backend,
        sheets: sheetsBackend,
        sharedRowCache: id => backend.rowHandle(id),
        durableCellRoute: "backend",
        modelCache: this.#modelCache,
        rowHeightPreferences: this.#preferences.rowHeight(),
        eventsTable,
        durableWrites: this.#durableWrites,
        page: this.#page,
        lifecycle: this.#lifecycle,
        evict: (handle) => this.#cores.evict(handle),
        listed: (sheet) => this.#sheets.peek(sheet),
        mirrorRow: (id, change) => this.#mirrorHeldRow(id, change),
        rowWrites,
        listenerErrors,
      };
      const chatPersistence = new ChatSessionStore(config.storage, null);
      this.#chats = new ChatsClient({
        baseUrl: baseUrl ?? "",
        accessToken: config.accessToken ?? null,
        persistence: chatPersistence,
        fetch: config.fetch ?? null,
        lifecycle: this.#lifecycle,
      });
      this.#weatherLocations = new WeatherLocationsLoader(baseUrl ?? null, config.fetch ?? null);
      this.#themes = new ThemesClient(
        config.storage,
        config.host,
        invalidation,
        ThemesClient.persistDurable(api, this.#durableWrites),
        config.fetch ?? null,
        this.#lifecycle,
      );
      // Tear down the live handle + rows-side cache when a sheet is deleted —
      // otherwise a re-open returns the stale promoted `synced` handle and the
      // sheet can't be re-COWed in-session. See `OpenSheetCores.close`.
      this.#sheets = new SheetsClient(
        api,
        invalidation,
        this.#durableWrites,
        (sheetId) => this.#cores.close(sheetId),
        this.#lifecycle,
      );
      this.#rows = new RowsClient(
        api,
        config.host,
        invalidation,
        (id, change) => this.#mirrorHeldRow(id, change),
        indexCtx,
        // Hand the Client-scoped registries to RowsClient so
        // `open(":lookupKey")` can short-circuit the bare-row GET
        // when a sheet open has already resolved + stored the row.
        // No new caches — reuses the same `deps.ids` + `deps.rows`
        // every other consumer reads from.
        {
          ids: idsReg,
          rows: this.#deps.rows,
          durableWrites: this.#durableWrites,
          writes: rowWrites,
          listenerErrors,
          rowHeights: this.#preferences.rowHeight(),
          clientRowIds: () => this.#clientRowIds,
        },
        // Standalone rows share the backend-registry handles with
        // sheet accessors (one handle per cell-access key); one on a
        // canonical key reads the client's canonical cell store.
        (rowId) => this.#standaloneRowCache(rowId),
        this.#dataSource,
        this.#lifecycle,
        (sheetId, lookupKey, actor) => this.#listLinked(sheetId, lookupKey, actor),
      );
      this.#events = new EventsClient(api, this.#rows, eventsTable, this.#lifecycle, config.appearance);
      const dataSource = this.#dataSource;
      this.#tasks = new TasksClient(
        api,
        this.#rows,
        invalidation,
        {
          writeCell: (rowKey, date, data, cellId, actor) => dataSource.applyCellEditLocal(rowKey, date, data, cellId, actor),
          clearCell: (rowKey, date, cellId, actor) => dataSource.clearCellLocal(rowKey, date, cellId, actor),
        },
        this.#lifecycle,
      );

      // The canonical store->handle bridge re-wires onto the accessor's
      // client-shared store, and the preference layer bumps its row-identity
      // ledger (L2) so pref/tz setters invalidate the same rows the sheet
      // stores do.
      this.#dataSource.attachCanonicalStore(this.#modelCache.canonicalCells);
      this.#dataSource.attachOrdinaryStore(this.#modelCache.ordinaryCells);
      this.#preferences.attachLedger(this.#modelCache.bumper);
      // The host keeps the hook until `dispose` uninstalls it (and it is
      // inert past `dispose` either way: the client flushes nothing it no
      // longer owns).
      this.#uninstallPersistenceFlush = config.host.installPersistenceFlush(() => {
        if (this.#lifecycle.disposed) return;
        this.#modelCache.flushPendingCellWrites();
      });
      this.#debounceBinding = {
        binding,
        unsubscribe: binding.failures.subscribe(
          (failure) => this.#routeDurableFailure(failure),
        ),
      };
    }

    /**
     * Release what this client holds beyond itself: its
     * `ClientConfig.debounceExecutor` binding and refusal subscription (a
     * `browser()` binding otherwise keeps the page's executor bound to this
     * client's origin, and keeps routing every refusal to it), its
     * `onUnclaimedDurableFailure` listeners, its persistence-flush hook
     * (uninstalled from its `ClientConfig.host`) and its sync-status polls
     * (`SyncKeyWatcher.dispose`: no key polls afterwards) — and tears down every sheet handle it has open
     * (each fails its active cell subscriptions and leaves the cache, as its
     * last `dispose` would). Afterwards every client verb — `open`,
     * `openBuiltin`, `openEphemeral`, `parseSheet`, `httpBackend`,
     * `setCurrentUser`, `schedulingLinksFor`, the `platformOf(client)` writers
     * — every facade (`sheets`, `rows`, `events`, `calendars`, `tasks`,
     * `preferences`, `user`, `chats`, `themes`: reading the property throws)
     * and every durable write throws `LonaSdk.Client.DisposedError`. Dispose
     * is total: a facade taken before it (`const tasks = client.tasks`), a
     * scheduling-links store, a chat session and a row opened through
     * `client.rows.open` share the client's lifecycle, so each of their
     * network verbs refuses the same way, before any local change; an
     * `open`, `rows.open`, `sheets.list` or `platformOf(client).sheet` whose
     * request was still out rejects with it and hands out nothing.
     * Idempotent. Also `[Symbol.dispose]`, so a CLI, a test or a server
     * request writes `using client = new LonaSdk.Client({...})`.
     *
     * A host callback that throws while the disposal notifies it (a
     * standalone row's cell read `onError`, a sheet cell's `onError`)
     * stops nothing: every read is still notified, every open sheet torn
     * down and the executor binding released. Then `dispose` throws one
     * `AggregateError` whose `errors` are every thrown value, in order —
     * after the client is fully disposed, so a retry has nothing left to
     * do and returns at once.
     */
    /**
     * The eviction guards the backend's row caches hold on the client-scoped
     * ordinary store: released at dispose with the caches themselves, so the
     * store (reachable as long as anything holds the client) keeps none alive.
     */
    readonly #cacheGuards = new Set<Unsubscribe>();

    dispose(): void {
      if (this.#lifecycle.disposed) return;
      const failures = new DisposeFailures();
      failures.run(() => this.#lifecycle.dispose());
      // One teardown per handle, whatever its aliases and holds — the
      // `parseSheet` handles too; each teardown evicts itself from
      // the open cores (`OpenSheetCores.evict`).
      for (const handle of this.#cores.all()) {
        failures.run(() => handle.teardown());
      }
      this.#cores.opened.clear();
      this.#cores.parsed.clear();
      // Before the executor's refusal channel is dropped: a durable write
      // still waiting on its verdict — a cell, row, sheet or event write,
      // in whichever registry — could otherwise hear none and settle as
      // accepted. It settles as unanswered instead.
      failures.run(() => this.#durables.failAll());
      failures.run(() => this.#dataSource.dispose());
      const bound = this.#debounceBinding;
      this.#debounceBinding = null;
      if (bound != null) {
        failures.run(() => bound.unsubscribe());
        failures.run(() => bound.binding.release());
      }
      const uninstall = this.#uninstallPersistenceFlush;
      this.#uninstallPersistenceFlush = null;
      if (uninstall != null) failures.run(uninstall);
      this.#unclaimedDurableFailures.clear();
      // Every sync-status key this client still polls — a rows-side sheet's
      // the model cache kept, a row's legacy sync state — stops now.
      failures.run(() => SyncKeyWatcher.for(this.syncPoll).dispose());
      // The client-wide row-handle registry goes with the client.
      for (const release of this.#cacheGuards) failures.run(release);
      this.#cacheGuards.clear();
      failures.run(() => this.#deps.backend.releaseRowHandles());
      failures.rethrow("LonaSdk.Client.dispose: a disposal callback threw");
    }

    [Symbol.dispose](): void {
      this.dispose();
    }

    /** Throw when `dispose` ran: `verb` names what was attempted. */
    #assertLive(verb: string): void {
      this.#lifecycle.assertLive(verb);
    }

    /** The viewer's sheet list (`SheetsClient`). Throws
     *  `LonaSdk.Client.DisposedError` once `dispose` ran. */
    get sheets(): SheetsClient {
      this.#assertLive("sheets");
      return this.#sheets;
    }

    /** Rows by key (`RowsClient`). Throws `LonaSdk.Client.DisposedError`
     *  once `dispose` ran. */
    get rows(): RowsClient {
      this.#assertLive("rows");
      return this.#rows;
    }

    /** Preferred domain-level facade for event operations (`EventsClient`).
     *  Throws `LonaSdk.Client.DisposedError` once `dispose` ran. */
    get events(): EventsClient {
      this.#assertLive("events");
      return this.#events;
    }

    /** The viewer's calendars (`CalendarsClient`). Throws
     *  `LonaSdk.Client.DisposedError` once `dispose` ran. */
    get calendars(): CalendarsClient {
      this.#assertLive("calendars");
      return this.#calendars;
    }

    /** The viewer's tasks (`TasksClient`). Throws
     *  `LonaSdk.Client.DisposedError` once `dispose` ran. */
    get tasks(): TasksClient {
      this.#assertLive("tasks");
      return this.#tasks;
    }

    /** The viewer's preferences (`PreferencesClient`). Throws
     *  `LonaSdk.Client.DisposedError` once `dispose` ran. */
    get preferences(): PreferencesClient {
      this.#assertLive("preferences");
      return this.#preferences;
    }

    /** What this client's zone loads fell back to or built from the platform (`ZoneOutcomes`). Throws once `dispose` ran. */
    get zones(): ZoneOutcomes {
      this.#assertLive("zones");
      return this.#zones;
    }

    /** The viewer's account reads (`UserClient`). Throws
     *  `LonaSdk.Client.DisposedError` once `dispose` ran. */
    get user(): UserClient {
      this.#assertLive("user");
      return this.#user;
    }

    /** Chat agent sessions (`ChatsClient`). Throws
     *  `LonaSdk.Client.DisposedError` once `dispose` ran. */
    get chats(): ChatsClient {
      this.#assertLive("chats");
      return this.#chats;
    }

    /** Themes (`ThemesClient`). Throws `LonaSdk.Client.DisposedError` once
     *  `dispose` ran. */
    get themes(): ThemesClient {
      this.#assertLive("themes");
      return this.#themes;
    }

    /**
     * The weather locations a weather row can pick from: the server's
     * `locations.json` asset, fetched through this client's `baseUrl` and
     * `fetch` on the first call and memoized for the client (another client
     * loads its own). A failed load rejects and is not kept: the next call
     * asks again. Resolves a frozen `WeatherLocations`. Throws
     * `LonaSdk.Client.DisposedError` once `dispose` ran.
     */
    getWeatherLocations(): Promise<WeatherLocations> {
      this.#assertLive("getWeatherLocations");
      return this.#weatherLocations.load();
    }

    /**
     * The weather table `alias.default-weather` reads the viewer's place
     * from; before it has loaded, `null`, and the first such read starts the
     * load, whose settling re-expands that alias (its preference path) so it
     * takes the place it now resolves. A failed load leaves it with none.
     */
    #viewerWeatherTable(): Option<WeatherLocations> {
      const table = this.#weatherLocations.peek();
      if (table == null && !this.#viewerWeatherLoadStarted) {
        this.#viewerWeatherLoadStarted = true;
        this.#weatherLocations.load().then(
          () => this.invalidation.emit(new CalendarPreferencesChanged(VIEWER_WEATHER_ACTOR, "defaultWeatherLocation")),
          () => {},
        );
      }
      return table;
    }

    /**
     * The weather-location table `getWeatherLocations()` loaded, or `null`
     * while none has (a synchronous read; a surface shows
     * `WeatherLocations.FALLBACK` meanwhile). Throws
     * `LonaSdk.Client.DisposedError` once `dispose` ran.
     */
    peekWeatherLocations(): Option<WeatherLocations> {
      this.#assertLive("peekWeatherLocations");
      return this.#weatherLocations.peek();
    }

    /**
     * Subscribe to the durable-write refusals no write of this client
     * claimed: not an `events.updateDurable`, `row.updateDurable` or durable
     * cell write still in flight (those reject their own `settled`, their
     * change taken back or re-read). That is a refused `sheets.updateDurable`
     * or row PATCH whose write had already settled (the client has already
     * re-read the sheet or row), a theme patch, or a `browser()` executor's
     * report of another client's write. The place for a host's recovery UI
     * (a reconnect prompt). Returns the unsubscribe.
     */
    onUnclaimedDurableFailure(
      listener: (failure: DebounceExecutor.Failure) => void,
    ): Unsubscribe {
      this.#unclaimedDurableFailures.add(listener);
      return () => this.#unclaimedDurableFailures.delete(listener);
    }

    /** Route one refused durable request (`ClientConfig.debounceExecutor`). */
    #routeDurableFailure(failure: DebounceExecutor.Failure): void {
      // A refused row PATCH leaves the row showing attributes the server
      // does not hold, whether or not a write in flight claims it: re-read.
      // A refused row or sheet PATCH leaves the cache showing what the
      // server does not hold, whether or not a write in flight claims it:
      // re-read it.
      const rowKey = refusedRowUpdateKey(failure.url);
      if (rowKey != null) void this.#rereadRow(rowKey);
      const sheetKey = refusedSheetUpdateKey(failure.url);
      if (sheetKey != null) void this.#rereadSheet(sheetKey);
      // Every registry marks every write the refusal carried (invariant
      // (iii)); a key belongs to one of them, never two.
      if (this.#durables.report(failure)) return;
      if (this.#unclaimedDurableFailures.size === 0) {
        Tracing.warn("net")(
          `durable write refused, no listener: ${failure.status} ${failure.url}`,
        );
        return;
      }
      for (const listener of this.#unclaimedDurableFailures) listener(failure);
    }

    /**
     * Replace a registered row's fields with the server's after a refused
     * `PATCH /api/rows/{rowKey}` (`row.updateDurable`), whose change the row
     * already shows: an authoritative read through the client's row-field
     * ledger (`RowWrites.beginRead` / `endRead`) — the server's row becomes
     * the confirmed state of the row's writes in flight, which stay applied
     * over it (a later refusal restores the re-read value, never an older
     * capture), and every field that changed is shown in every copy of the
     * row and announced once, as `ROW_REREAD_ACTOR`. Every open handle
     * holding the row then re-derives what its structure reads from the
     * row's fields (`local.update`).
     */
    async #rereadRow(rowKey: string): Promise<void> {
      const localId = this.#deps.ids.get(RowKey.of(rowKey));
      if (localId == null) return;
      const row = this.#deps.rows.get(localId);
      if (row == null) return;
      // Opened before the GET: a write that lands while it is in flight
      // lays over its answer, which may predate it.
      const rowRead = this.#deps.rowWrites.ledger.beginRead(localId);
      let fresh: Option<Network.SheetRowDb> = null;
      try {
        fresh = await SdkRequestError.checkRequest(
          "rows",
          "get",
          "GET",
          "/api/rows/{rowKey}",
          () =>
            SdkRequestError.notFoundAsNull(() =>
              this.#deps.api.getResult("/api/rows/{rowKey}", {
                path: { rowKey: RowNetworkKey.forRead(RowNetworkKey.fromWire(rowKey)) },
              })
            ),
        );
      } catch (err) {
        Tracing.warn("net")(
          `row re-read after a refused update failed: ${String(err)}`,
        );
      }
      try {
        const shown = this.#deps.rowWrites.ledger.endRead(
          rowRead,
          fresh == null ? null : rereadFields(fresh, row),
          ROW_REREAD_ACTOR,
        );
        if (!shown) return;
        const raw = row.raw;
        const patch = {
          label: Patch.fromWire(raw.label ?? null),
          heightPx: Patch.fromWire(raw.heightPx ?? null),
          formula: Patch.fromWire(raw.formula ?? null),
          attributes: Patch.fromWire(Json.object(raw.attributes ?? null)),
          icon: Patch.fromWire(raw.icon ?? null),
          lookupKey: Patch.fromWire(raw.lookupKey ?? null),
        } satisfies SheetHandle.LocalRowPatch;
        for (const handle of this.#cores.opened.values()) {
          handle.platformView().local.update(localId, patch, SheetHandle.ACTOR);
        }
      } catch (err) {
        Tracing.warn("net")(
          `row re-read after a refused update failed: ${String(err)}`,
        );
      }
    }

    /**
     * Replace the cached sheet with the server's after a refused
     * `sheets.updateDurable`, whose attributes the cache already shows.
     */
    async #rereadSheet(sheetKey: string): Promise<void> {
      try {
        // Through the sheet's pending-write model: the answer never erases
        // a metadata write still in flight or one that landed meanwhile.
        await rereadSheetMetadata(this.#sheets, sheetKey);
      } catch (err) {
        Tracing.warn("net")(
          `sheet re-read after a refused update failed: ${String(err)}`,
        );
      }
    }

    /** Bind the current logged-in user. Called once by the app at login: the
     *  principal an ephemeral built-in sheet is seeded under, and the user a
     *  bare `:lookup` resolves against in `client.sheetKeys`. */
    setCurrentUser(userId: UserId): void {
      this.#assertLive("setCurrentUser");
      this.#deps.ids.setCurrentUser(userId);
    }

    /**
     * The scheduling links (booking pages) of `userId` — one store per user,
     * created on first use. The user is an argument, not state bound
     * earlier: `const links = client.schedulingLinksFor(user.id);`.
     */
    schedulingLinksFor(userId: UserId): SchedulingLinksClient {
      this.#assertLive("schedulingLinksFor");
      let links = this.#schedulingLinksByUser.get(userId);
      if (links == null) {
        links = schedulingLinksClientFor({
          api: this.#deps.api,
          ownerId: userId,
          invalidation: this.#deps.invalidation,
          assertLive: (verb) => this.#lifecycle.assertLive(verb),
        });
        this.#schedulingLinksByUser.set(userId, links);
      }
      return links;
    }



    /**
     * Read-only view of the client-scoped `SheetRow` registry: the row
     * metadata (label, type, attributes, descriptors, height) every open
     * `SheetHandle` renders from. Write through
     * `platformOf(client)` (`@tento/lona/host`): `registerRow`,
     * `registerDescriptorOverrides` and `applyStoredRowHeights`.
     */
    get rowRegistry(): RowRegistryReads {
      return this.#rowRegistryView;
    }

    readonly #rowRegistryView: RowRegistryReads = Object.freeze({
      peek: (id: RowLocalId) => {
        const row = this.#deps.rows.get(id);
        return row == null ? null : this.#deps.rowFaces.of(row);
      },
      has: (id: RowLocalId) => this.#deps.rows.get(id) != null,
      iter: () => this.#deps.rowFaces.entries(this.#deps.rows.iter()),
    });

    /**
     * This client's row-identity scope, resolve-only: every sheet and row it
     * builds interns through it (`client.ids.resolve(RowKey.of(key))`,
     * `client.ids.resolveFromRawId(raw)`). The registry's frozen
     * `RowLocalId.resolverOf` face — the object `platformOf(client).ids` and
     * every sheet's `registry` hand out too; `instanceof RowLocalId.Registry`
     * is false, and no cast reaches `alias` / `rekey` / `setCurrentUser`.
     */
    get ids(): RowLocalId.Resolver {
      return RowLocalId.resolverOf(this.#deps.ids);
    }

    /**
     * The rows-side transaction log (TX0), read-only: the read mark `tau()`,
     * the retained sealed transactions and the counters — what a host joins
     * its invalidation frames against (`TxFrameJoin`). A frozen forwarder
     * built once per client: nothing here records, and `transactions()`
     * answers a frozen copy of the retained window.
     */
    get txLog(): LonaSdk.Client.TxLogReads {
      return this.#txLogReads ??= txLogReadsOf(this.#modelCache.txLog);
    }

    #txLogReads: Option<LonaSdk.Client.TxLogReads> = null;


    /**
     * Live map of every sync-key poll this client's cell backend has run
     * (`syncKey → SyncPollEntry`), for debug surfaces, as a frozen read face
     * (`get` / `addListener` / `removeListener` — no cast reaches the
     * backend's writer). `null` when the backend never polls.
     */
    get syncPollState(): Option<
      LiveData.Readable<ReadonlyMap<SyncKey, SyncPollEntry>>
    > {
      const live = this.#deps.backend.syncPollState;
      if (live == null) return null;
      if (this.#syncPollReads?.live !== live) this.#syncPollReads = { live, reads: syncPollReadsOf(live) };
      return this.#syncPollReads.reads;
    }

    /** `syncPollState`'s read face, rebuilt only if the backend's live map changes. */
    #syncPollReads: Option<{
      readonly live: LiveData.Readable<ReadonlyMap<SyncKey, SyncPollEntry>>;
      readonly reads: LiveData.Readable<ReadonlyMap<SyncKey, SyncPollEntry>>;
    }> = null;

    /**
     * Build a rows-side (`@tento/lona/sheets`) HTTP backend over this client's
     * API — the `backend` a live, server-backed host returns from
     * `ClientConfig.rowsOptions`. A fresh instance per call; callers never handle the
     * typed API.
     */
    httpBackend(): RowsHttpBackend {
      this.#assertLive("httpBackend");
      return RowsHttpBackend.withVerdicts(this.#api, this.#cellVerdicts);
    }

    /**
     * The verdict channel of the rows-side backends this client hands out
     * (`httpBackend()`): a handed-off cell write is claimed in the client's
     * durable-write registry under its own request id, so the refusal of the
     * execution that carried it comes back to the write (its ledger takes the
     * paint back) instead of reaching no one, and is reported to the host's
     * `requestErrorHandler` once.
     */
    readonly #cellVerdicts: RowsHttpBackend.CellVerdicts = {
      handOff: async (key, send) => {
        const requestId = this.#durableWrites.nextRequestId();
        const failure = await this.#durableWrites.handOff(key, requestId, () => send(requestId));
        if (failure == null) return null;
        return { failure: DurableHandoffs.verdict(failure), reason: failure.reason ?? "", details: failure.details ?? "" };
      },
      refused: (error) => this.#requestErrorHandler(error),
    };


    /**
     * Build a `SheetHandle` from a sheet payload the host already holds (a
     * dump, a boot payload, a template preview, a test fixture), bound to this
     * client's registries, api and sink. `open(key)` is the fetching path;
     * this one never touches the network. `config` is required:
     * `SheetHandle.ParseConfig.DEFAULT` reads through the client's own seams;
     * `config.backend` / `config.rowsBackend` read the handle through preview
     * backends instead of the client's (see `SheetHandle.ParseConfig`). The
     * handle is never cached for `open`, but the client's `dispose` tears it
     * down with the open sheets.
     */
    parseSheet(
      data: SheetHandle.ParseInput,
      config: SheetHandle.ParseConfig,
    ): SheetHandle {
      this.#assertLive("parseSheet");
      const core = SheetHandleCore.parse(data, this.#depsFor(config), config, this.#cores.abandonParsed);
      this.#cores.parsed.add(core);
      return leaseOver(core, true);
    }

    /**
     * The sheets backend of a preview without a rows backend: every read
     * rejects, naming the preview, except the keyed reads `keyedReads`
     * answers when the caller supplied one.
     */
    #previewSheets(keyedReads: Option<Pick<SheetsBackend, "fetchCellsByKey">>): SheetsBackend {
      const unreachable = unreachableSheetsBackend("LonaSdk.Client.parseSheet preview");
      if (keyedReads == null) return unreachable;
      return { ...unreachable, fetchCellsByKey: (key, days) => keyedReads.fetchCellsByKey(key, days) };
    }

    /**
     * A preview's sheets backend. Its keyed and row reads go to its own rows
     * side, else to `keyedReads`, else reject (`#previewSheets`). Its bulk
     * read always has an answer: a `SeededSyncBackend`'s seeds (those rows
     * exist only in the seed), else the rows side, else the preview's cell
     * backend's own (`CellCachingBackend.sheetReads`), else the client's —
     * a preview built from published options never holds a bulk read that
     * rejects.
     */
    #previewSheetsFor(
      rowsBackend: Option<SheetsBackend>,
      keyedReads: Option<Pick<SheetsBackend, "fetchCellsByKey">>,
      cellBackend: Option<CellCachingBackend>,
    ): SheetsBackend {
      if (cellBackend instanceof SeededSyncBackend) {
        return this.#withBulkRead(rowsBackend ?? this.#previewSheets(keyedReads), cellBackend.sheetReads());
      }
      if (rowsBackend != null) return rowsBackend;
      return this.#withBulkRead(this.#previewSheets(keyedReads), cellBackend?.sheetReads() ?? this.#deps.sheets);
    }

    /**
     * `sheets` with its bulk read (`fetchSheetCells`) answered by `reads`;
     * every other read stays on `sheets`.
     */
    #withBulkRead(sheets: SheetsBackend, reads: Pick<SheetsBackend, "fetchSheetCells">): SheetsBackend {
      const bulkRead: SheetsBackend["fetchSheetCells"] = (sheetKey, range) => reads.fetchSheetCells(sheetKey, range);
      return new Proxy(sheets, {
        get: (target, prop) => {
          if (prop === "fetchSheetCells") return bulkRead;
          const value: unknown = Reflect.get(target, prop);
          return typeof value === "function" ? value.bind(target) : value;
        },
      });
    }

    /**
     * The client's deps with `config`'s preview seams swapped in. The
     * registries, api and platform are always the client's.
     */
    #depsFor(config: SheetHandle.ParseConfig): SheetHandleDeps {
      const backend = config.backend;
      const rowsBackend = config.rowsBackend;
      const keyedReads = config.keyedReads;
      const invalidation = config.invalidation;
      if (backend == null && rowsBackend == null && keyedReads == null && invalidation == null) {
        return this.#deps;
      }
      invariant(
        keyedReads == null || rowsBackend == null,
        "LonaSdk.Client.parseSheet: `keyedReads` is for a preview without a " +
          "`rowsBackend` — the rows backend answers the preview's keyed reads",
      );
      invariant(
        invalidation == null || rowsBackend != null,
        "LonaSdk.Client.parseSheet: an `invalidation` override needs a " +
          "`rowsBackend` — the rows-side sheet publishes into the same sink",
      );
      const sink = invalidation ?? this.#deps.invalidation;
      const cellBackend = backend == null
        ? this.#deps.backend
        : CellCachingBackend.of(backend, "LonaSdk.Client.parseSheet");
      const modelCache = rowsBackend == null
        ? this.#deps.modelCache
        : new SheetModelCache({
          backend: rowsBackend,
          listenerErrors: this.#listenerErrors,
          registry: this.#deps.ids,
          invalidationSink: sink,
          builtinAliases: this.#rowAliases,
          syncPoll: this.syncPoll,
          registries: this.#deps.modelCache.registries,
        });
      // A preview's own fan-out installs bulk answers into the stores its
      // views read (the model cache's), as the client's does.
      const dataSource = new SheetDataSource(
        sink,
        this.#deps.ids,
        (rowId) => cellBackend.rowHandle(rowId),
        this.#listenerErrors,
        (rowId) => cellBackend.peekRowHandle(rowId),
      );
      dataSource.attachCanonicalStore(modelCache.canonicalCells);
      dataSource.attachOrdinaryStore(modelCache.ordinaryCells);
      return {
        ...this.#deps,
        invalidation: sink,
        backend: cellBackend,
        // A preview's keyed reads go to its own rows side, or to the keyed
        // source it was given. One with neither rejects rather than reach the
        // client's.
        sheets: this.#previewSheetsFor(rowsBackend, keyedReads, backend == null ? null : cellBackend),
        // A preview's row caches are its backend's too (not the client's
        // shared ones), so its cells and fetches stay in the preview.
        sharedRowCache: (id) => cellBackend.rowHandle(id),
        // A preview's cells stay in the preview: its own fan-out over its own
        // backend, never the client's shared one.
        dataSource,
        ownsDataSource: true,
        modelCache,
        rowWrites: rowsBackend == null ? this.#deps.rowWrites : this.#rowFieldWrites(modelCache),
      };
    }

    /**
     * A take-back the row-field ledger abandoned because this client was
     * disposed (`RowWrites.bindOwner`): the refused write's fields a later
     * PATCH carried may stay on the server. Reported to the client's
     * `requestErrorHandler` as a `RowWriteError("update")` (a handler that
     * throws is traced: nothing throws into a disposed client's caller).
     */
    #reportAbandonedTakeBack(row: RowLocalId, fields: ReadonlySet<string>, refusal: RowWriteError): void {
      const report = RowWriteError.after(
        refusal,
        "update",
        "PATCH",
        "/api/rows/{rowKey}",
        `the take-back of row ${row.rawId}'s ${[...fields].join(", ")} was abandoned: the client was disposed, so the server may keep the refused value`,
      );
      const handler = unobservedRefusalsOf(this.#lifecycle);
      try {
        if (handler != null) handler(report);
        else Tracing.warn("net")(report.message);
      } catch (thrown) {
        Tracing.warn("net")(`the requestErrorHandler threw on an abandoned take-back: ${String(thrown)}`);
      }
    }

    /**
     * The SDK's side of `cache`'s row-field ledger (`RowFieldWrites`): the
     * client's row copies as a display of it, its rename and lookup-key
     * follow-ups, and the client's HTTP rows backend as the one transport.
     */
    #rowFieldWrites(cache: SheetModelCache): RowFieldWrites {
      // The ledger's take-backs follow this client's lifecycle: once it is
      // disposed a due take-back is not sent, and the abandoned correction
      // goes to its `requestErrorHandler`.
      cache.rowWrites.bindOwner({
        live: () => !this.#lifecycle.disposed,
        abandoned: (row, fields, refusal) => this.#reportAbandonedTakeBack(row, fields, refusal),
      });
      return new RowFieldWrites(
        cache.rowWrites,
        this.#rowsHttp,
        (id) => this.#deps.rows.get(id),
        {
          relabel: (id, from, to) => this.#relabel(id, from, to),
          lookupKey: (id, from, to) => this.#mirrorLookupKey(id, from, to),
        },
      );
    }

    #promotionWrites: Option<PromotionWrites> = null;

    /** The promotion's requests (`PromotionWrites`), over this client's API and sheets client. */
    #promotion(): PromotionWrites {
      return this.#promotionWrites ??= new PromotionWrites(this.#deps.api, () => this.#sheets);
    }

    #openEphemeralHandle(
      ephemeral: LonaSdk.EphemeralSheet,
      cacheKey: Option<string>,
      mode: SheetHandle.EphemeralMode,
      factory: Option<SystemRow.Factory>,
    ): SheetHandleCore {
      // A signed-out client seeds under the pending-row owner (`UserId.LOCAL`).
      const principal: UserId = this.#deps.ids.currentUser ?? UserId.LOCAL;
      const handle = withEphemeralSeed({
        backend: [principal, this.#deps.backend, this.#listenerErrors, this.#deps.sharedRowCache],
        registries: this.#modelCache.registries,
        hold: (key) => this.#modelCache.ordinaryCells.retain(this.#deps.ids.resolve(key)),
      }, ephemeral, (mock) => {
        // A keyed read (a row with a lookupKey) goes to the client's rows
        // backend, as the seeded backend's real-backend delegation did: seeded
        // rows are uuid rows, which are read by id from the seeded backend and
        // never by key, so the seed has nothing to answer a key with.
        const deps: SheetHandleDeps = {
          ...this.#deps,
          backend: mock,
          sheets: this.#withBulkRead(this.#deps.sheets, mock.sheetReads()),
          durableCellRoute: "api",
        };
        const sheet = ephemeralWireSheet(ephemeral, principal, specNetworkId(ephemeral.id));
        return SheetHandleCore.parse(sheet, deps, {
          ...SheetHandle.ParseConfig.DEFAULT,
          mode,
          factory,
        }, this.#cores.abandonParsed);
      });

      handle.installPromoter((sheet) =>
        ephemeralPromoter(sheet, {
          ids: this.#deps.ids,
          writes: () => this.#promotion(),
          onSheetPromoted: this.#onSheetPromoted,
          rehome: (oldId, lookupKey) => {
            this.#cores.opened.delete(oldId);
            this.#cores.opened.set(handle.network.id, handle);
            if (cacheKey) this.#cores.opened.set(cacheKey, handle);
            if (lookupKey != null) this.#cores.opened.set(lookupKey, handle);
          },
        }),
      );
      this.#cores.opened.set(handle.network.id, handle);
      if (cacheKey) this.#cores.opened.set(cacheKey, handle);
      if (ephemeral.lookupKey != null) this.#cores.opened.set(ephemeral.lookupKey, handle);
      return handle;
    }

    /**
     * Open a sheet the caller names by a typed reference: an entry of
     * `client.#sheets.list()`, a `SheetKey`, or a `SheetId`. A sheet the
     * server does not have throws `SheetNotFoundError` — a typed open never
     * seeds a built-in sheet (that is `openBuiltin`). Cached: a second open
     * of a sheet whose handle is still live returns it.
     *
     * ```ts
     * const [first] = await client.#sheets.list();
     * using handle = await client.open(first);
     * ```
     */
    async open(sheet: Network.UserSheet | SheetKey | SheetId): Promise<SheetHandle> {
      this.#assertLive("open");
      if (sheet instanceof SheetKey || sheet instanceof SheetId) {
        return this.#openByKey(sheet.toString(), false);
      }
      return this.#openByKey(String(sheet.id), false);
    }

    /**
     * Open a built-in sheet by its catalog name (`":~lona:home"`,
     * `":~lona:journal"`, …): the user's stored copy when the server has one,
     * else a freshly seeded ephemeral sheet (promoted on its first edit). A
     * name outside the catalog throws `SheetNotFoundError`.
     */
    async openBuiltin(name: string): Promise<SheetHandle> {
      this.#assertLive("openBuiltin");
      if (BUILTIN_SHEET_CATALOG[name] == null) {
        throw new SheetNotFoundError({ sheetKey: SheetNetworkKey.fromWire(name), status: 404 });
      }
      return this.#openByKey(name, true);
    }

    /**
     * Open an ephemeral (client-minted, not yet persisted) sheet from `spec`.
     * When no sheet is open for `spec.id`, `spec` is parsed afresh.
     * `onExisting` decides what a sheet already open for it means:
     * `"reuse"` returns a handle on it as it is (its content may differ from
     * `spec`); `"exclusive"` throws `SheetHandle.InUseError` — an open sheet
     * always has a holder, and one caller never tears down a sheet another
     * holds; dispose every handle on it first. `factory` selects the system
     * rows (`SystemRow.Factory.PREVIEW` for a header-only embed; `null` for
     * the full editable chrome).
     */
    async openEphemeral(
      spec: EphemeralSheet,
      options: EphemeralOpenOptions,
    ): Promise<SheetHandle> {
      this.#assertLive("openEphemeral");
      const ephemeralId = SheetId.of(String(spec.id));
      const cached = this.#cores.opened.get(ephemeralId.toString());
      if (cached != null) {
        if (options.onExisting === "reuse") return leaseOver(cached, true);
        // `exclusive`: never tear down a sheet another caller holds.
        throw new SheetHandle.InUseError(ephemeralId, cached.holders);
      }
      return leaseOver(
        this.#openEphemeralHandle(spec, null, options.mode, options.factory),
        true,
      );
    }

    /**
     * `open` by a server key; `seedBuiltin`: a miss on a catalog name seeds
     * it. One open sheet per key: an open sheet is leased again, and an
     * open of the key already in flight is joined — two concurrent opens
     * share one sheet (one core), so the client's `dispose` reaches both
     * handles. An open that seeds a built-in and one that does not are not
     * the same read: the later waits the earlier out, then looks again.
     */
    async #openByKey(arg: string, seedBuiltin: boolean): Promise<SheetHandle> {
      const verb = seedBuiltin ? "openBuiltin" : "open";
      for (;;) {
        // Check cache first (hits for previously-opened ephemeral sheets)
        const cached = this.#cores.opened.get(arg);
        if (cached) return leaseOver(cached, true);
        const pending = this.#pendingOpens.get(arg);
        if (pending == null) break;
        if (pending.seedBuiltin === seedBuiltin) {
          const core = await pending.core;
          this.#assertLive(verb);
          return leaseOver(core, true);
        }
        await pending.core.then(() => {}, () => {});
        this.#assertLive(verb);
      }
      const opening = { seedBuiltin, core: this.#readCore(arg, seedBuiltin) };
      this.#pendingOpens.set(arg, opening);
      try {
        return leaseOver(await opening.core, true);
      } finally {
        if (this.#pendingOpens.get(arg) === opening) this.#pendingOpens.delete(arg);
      }
    }

    /** The in-flight opens by key (`#openByKey`), joined by a concurrent
     *  open of the same key. */
    readonly #pendingOpens = new Map<string, { readonly seedBuiltin: boolean; readonly core: Promise<SheetHandleCore> }>();

    /** Read (or seed) the sheet `arg` names and register its core. */
    async #readCore(arg: string, seedBuiltin: boolean): Promise<SheetHandleCore> {
      // Checked read: only an explicit miss (404, or a 200 `null` body) is
      // "no sheet"; a 401 / 5xx / transport failure throws `SheetOpenError`
      // instead of silently seeding a fresh ephemeral over the user's sheet.
      const { sheet: data, status } = await readSheetForOpen(
        this.#deps.api,
        arg,
      );
      // The client may have been disposed while the GET was out: its
      // teardown never saw this handle, so build and register
      // none — neither the server's nor a seeded built-in.
      this.#assertLive(seedBuiltin ? "openBuiltin" : "open");
      if (data == null) {
        const currentUser = this.#deps.ids.currentUser ?? "local";
        // The catalog seeds the ephemeral under THIS principal. Pass the raw
        // `currentUser` (not the `"local"` display fallback): a shared literal
        // is a shared `spreadsheets.id`, and the first account to promote it
        // would squat the id for every other account (the miss falls back to a
        // per-session scope instead). The server was already asked FIRST above,
        // so a user who has promoted never reaches this branch.
        const raw = seedBuiltin
          ? ephemeralFromCatalog(arg, this.#deps.ids.ownerScope)
          : null;
        const catalogEntry = BUILTIN_SHEET_CATALOG[arg];
        if (raw == null || catalogEntry == null) {
          throw new SheetNotFoundError({
            sheetKey: SheetNetworkKey.fromWire(arg),
            status,
          });
        }
        return this.#openEphemeralHandle(
          {
            ...SheetHandle.fromRaw(raw, UserId.of(currentUser)),
            creatorId: UserId.of(currentUser),
            lookupKey: arg,
            ...(catalogEntry.timeScale != null
              ? { timeScale: catalogEntry.timeScale }
              : {}),
          },
          arg,
          "ephemeral",
          null,
        );
      }
      const principal: UserId = this.#deps.ids.currentUser ?? UserId.LOCAL;
      const handle = SheetHandleCore.parse(
        withSeededReservedRows(data, principal, arg),
        this.#deps,
        null,
        this.#cores.abandonParsed,
      );
      this.#cores.opened.set(handle.network.id, handle);
      this.#cores.opened.set(arg, handle);
      return handle;
    }

    /**
     * The open `SheetHandle` of `sheet` (`SheetsClient.SheetRef`: a list
     * entry, a handle, a `SheetId` or a `SheetKey` — the id, the lookup key
     * or the catalog name it was opened by), or `null` when it is not open.
     * A view that holds nothing: its `dispose` releases nothing, and it is
     * refused (`SheetHandle.DisposedError`) once the sheet's last holder
     * released it. Each call hands a new view; compare handles by `id`.
     *
     * Unlike `sheets.peek(sheet)` (the server-list cache), this consults the
     * live handle registry — ephemeral and server-backed handles alike. Use
     * it when the question is "is this handle still alive?" rather than
     * "does the user have access to this sheet?".
     */
    peekOpenHandle(sheet: SheetsClient.SheetRef): Option<SheetHandle> {
      const key = sheet instanceof SheetKey || sheet instanceof SheetId
        ? sheet.toString()
        : String("network" in sheet ? sheet.network.id : sheet.id);
      const direct = this.#cores.opened.get(key);
      if (direct != null) return leaseOver(direct, false);
      // A `SheetId` in another spelling than the one the cache holds.
      if (sheet instanceof SheetId) {
        for (const handle of this.#cores.opened.values()) {
          if (handle.id === sheet) return leaseOver(handle, false);
        }
      }
      return null;
    }

    /**
     * The rows-side sheet the client's accessor holds under `key`: a
     * `SheetKey` by its own spelling; a `SheetId` in any spelling it was
     * opened with (canonical `ss_<hex>`, the UUID, or an ephemeral spec's
     * `ephemeral(<name>)`) — `SheetModelCache.peekById`.
     */
    #peekRowsSheet(key: SheetKey | SheetId): Option<Sheet> {
      if (key instanceof SheetKey) return this.#modelCache.peek(SheetNetworkKey.fromWire(key.toString()));
      return this.#modelCache.peekById(key);
    }

    /**
     * The white-box view behind `clientInstrumentationOf`
     * (`@tento-lona/internal-testing`): the full sheets the platform door
     * hands out as reads, and the transaction log's recorder.
     */
    #instrumentationDoor(): ClientInstrumentation {
      const client = this;
      return {
        get ids() {
          return client.#deps.ids;
        },
        get canonicalCells() {
          return client.#modelCache.canonicalCells;
        },
        get ordinaryCells() {
          return client.#modelCache.ordinaryCells;
        },
        get ledger() {
          return client.#modelCache.ledger;
        },
        clientRowIds: {
          get on() {
            return client.#clientRowIds;
          },
          set on(on: boolean) {
            client.#clientRowIds = on;
          },
        },
        sheetList: sheetListCache(client.#sheets),
        openSheet(key) {
          client.#assertLive("clientInstrumentationOf(client).openSheet");
          const open = client.#peekRowsSheet(key);
          if (open != null) return Promise.resolve(open);
          // A disposed client hands out no sheet its open overtook.
          return client.#modelCache.open(SheetNetworkKey.fromWire(key.toString())).then((sheet) => {
            client.#assertLive("clientInstrumentationOf(client).openSheet");
            return sheet;
          });
        },
        ephemeralSheet(spec) {
          client.#assertLive("clientInstrumentationOf(client).ephemeralSheet");
          return client.#modelCache.openEphemeral(
            toRawSheet(spec),
          );
        },
        peekSheet(key) {
          return client.#peekRowsSheet(key);
        },
        get txLog() {
          return client.#modelCache.txLog;
        },
        get rowRegistry() {
          return client.#deps.rows;
        },
      };
    }

    /** The door `platformOf(client)` hands out, built once (`#platformDoor`). */
    #platformCache: Option<LonaSdk.Client.Platform> = null;
    /**
     * The cache a standalone row (`client.rows.open` / `peek`) reads — the
     * backend registry's, shared with sheet accessors. A row on a canonical
     * key reads through the client's canonical cell store (`CellStoreView`),
     * the one store its sheets read, unless a sheet's binding gave it a view
     * already; a sheet-less row on another key has no store but its cache.
     */
    #standaloneRowCache(rowId: RowId): RowCellCache {
      const rh = this.#deps.backend.rowHandle(rowId);
      const key = rowId.toString();
      if (rh.view == null && isCanonicalKey(key)) {
        rh.setView(CellStoreView.of("canonical", this.#modelCache.canonicalCells, key, () => rh.cardinality, null));
      }
      return rh;
    }

    /** `platformOf(client).canonicalCells`' face, built once. */
    #canonicalCellReads: Option<LonaSdk.Client.CanonicalCellReads> = null;

    /**
     * The value behind {@link platformOf}; reached from the static block. A
     * frozen object built once per client, whose `ids`, `canonicalCells`,
     * `local` and `themes` are frozen faces too.
     */
    #platformDoor(): LonaSdk.Client.Platform {
      if (this.#platformCache != null) return this.#platformCache;
      const client = this;
      const door: LonaSdk.Client.Platform = {
        page: client.#page,
        get ids() {
          return RowLocalId.resolverOf(client.#deps.ids);
        },
        get canonicalCells() {
          return client.#canonicalCellReads ??= canonicalCellReadsOf(client.#modelCache.canonicalCells);
        },
        openSheet(key) {
          client.#assertLive("platformOf(client).openSheet");
          const open = client.#peekRowsSheet(key);
          if (open != null) return Promise.resolve(sheetReadsOf(open));
          // A disposed client hands out no sheet its open overtook.
          return client.#modelCache.open(SheetNetworkKey.fromWire(key.toString())).then((sheet) => {
            client.#assertLive("platformOf(client).openSheet");
            return sheetReadsOf(sheet);
          });
        },
        ephemeralSheet(spec) {
          client.#assertLive("platformOf(client).ephemeralSheet");
          return sheetReadsOf(client.#modelCache.openEphemeral(
            toRawSheet(spec),
          ));
        },
        peekSheet(key) {
          const open = client.#peekRowsSheet(key);
          return open == null ? null : sheetReadsOf(open);
        },
        registerRow(localId, row) {
          client.#assertLive("registerRow");
          client.#deps.rows.set(localId, row, null);
          return client.#deps.rowFaces.of(nonnull(
            client.#deps.rows.get(localId),
            `platformOf(client).registerRow: ${localId.toString()} did not register`,
          ));
        },
        local: Object.freeze<LonaSdk.Client.Local>({
          writeCell(row, target, data, actor) {
            client.#assertLive("local.writeCell");
            const cell = datedAddress(target);
            client.#dataSource.applyCellEditLocal(SheetHandle.RowRef.key(row), cell.date, data, cell.id, actor);
          },
          clearCell(row, target, actor) {
            client.#assertLive("local.clearCell");
            const cell = datedAddress(target);
            client.#dataSource.clearCellLocal(SheetHandle.RowRef.key(row), cell.date, cell.id, actor);
          },
        }),
        themes: Object.freeze<LonaSdk.Client.Themes>({
          seed(theme) {
            client.#assertLive("platformOf(client).themes.seed");
            seedThemes(client.#themes, theme);
          },
        }),
        registerDescriptorOverrides(overrides) {
          client.#deps.rows.descriptors.registerAll(overrides);
          client.#deps.rows.descriptors.applyAll(client.#deps.rows);
        },
        applyStoredRowHeights() {
          client.#preferences.rowHeight().applyToRegistry(client.#deps.rows);
        },
        closeSheet(sheetId) {
          client.#cores.close(sheetId);
        },
        invalidateColumn(column) {
          client.#dataSource.invalidateColumn(column);
        },
        seedBoot(boot) {
          client.#assertLive("platformOf(client).seedBoot");
          if (client.#seededBoot) {
            throw new Error("platformOf(client).seedBoot: this client was already seeded — seed once, at boot");
          }
          client.#seededBoot = true;
          seedCalendars(client.#calendars, boot.user, [...boot.calendars]);
          seedSheetList(client.#sheets, [...boot.sheets]);
        },
      };
      this.#platformCache = Object.freeze(door);
      return this.#platformCache;
    }

    /** Whether the host seeded this client (`platformOf(client).seedBoot`):
     *  a second seed throws. */
    #seededBoot = false;

    /**
     * Get a RowCellCache for a row key with index factories wired.
     * Fetches metadata if needed so factories (e.g., EventsIndex) are applied.
     * The caller can then access `rh.index.events` after fetching data.
     */
    /**
     * Get a RowCellCache for a row key with index factories wired.
     * Pass `rowType` if known (avoids an API call for reserved rows
     * that may not exist in the database), else `null`.
     */
    async #rowHandle(
      rowKey: string,
      rowType: Option<string>,
    ): Promise<RowCellCache> {
      const rowId = RowId.of(rowKey);
      if (!this.#deps.backend.rowMeta(rowId)) {
        if (rowType) {
          this.#deps.backend.storeRowMeta(rowId, SheetRowMeta.of(Network.SheetRowDb.make({
            id: rowId,
            type: rowType,
            ownerId: UserId.DETACHED,
            timeScale: "week",
            attributes: null,
            formula: null,
            heightPx: null,
            icon: null,
            label: null,
            lookupKey: null,
            metadata: null,
          }), SheetRowMeta.DETACHED), RowType.get(rowType).cellCardinality);
        } else {
          const networkRowKey = RowNetworkKey.forRead(RowNetworkKey.fromWire(rowKey));
          const raw = await SdkRequestError.checkRequest(
            "rows",
            "get",
            "GET",
            "/api/rows/{rowKey}",
            () =>
              SdkRequestError.notFoundAsNull(() =>
                this.#deps.api.getResult("/api/rows/{rowKey}", {
                  path: { rowKey: networkRowKey },
                })
              ),
          );
          if (raw) {
            this.#deps.backend.storeRowMeta(rowId, SheetRowMeta.of(raw, SheetRowMeta.DETACHED), RowType.get(raw.type).cellCardinality);
          }
        }
      }
      return this.#deps.backend.rowHandle(rowId);
    }

    /**
     * What a held row's write asks of the client beyond the row-field ledger
     * (`RowMirror`): propagate a rename (`#propagateRename`), or recover a
     * promote that may have stored (`#recoverPromoted`). Every copy of the
     * row is written by the ledger itself (`RowFieldWrites`).
     */
    #mirrorHeldRow(localId: RowLocalId, change: RowMirror.Change): Option<Promise<void>> {
      if ("mayHaveCreated" in change) {
        this.#recoverPromoted(change.mayHaveCreated);
        return null;
      }
      return this.#propagateRename(localId, change.renamed);
    }

    /**
     * Row `localId`'s new label, shown by the row-field ledger
     * (`RowFieldWrites.Hooks.relabel`): every open handle holding the row
     * re-derives its lookup maps and formula graph (`#labelChanged`) — for a
     * write through any facade of the row, and in every sheet that holds it.
     */
    #relabel(localId: RowLocalId, from: Option<string>, to: Option<string>): void {
      for (const core of this.#cores.all()) {
        if (core.disposed || core.rows.peek({ id: localId }) == null) continue;
        core.labelChanged(from, to);
      }
    }

    /**
     * A held or joined rename paints dependent formulas before the label.
     * Acceptance sends them; setup/label refusal rolls every rewrite back
     * despite failing observers. Sent writes settle individually: refused
     * ones are taken back and re-read while accepted ones stand. Resolves
     * after all settle, rejects with the first refusal, or null for no dependents.
     */
    #propagateRename(localId: RowLocalId, renamed: RowMirror.Renamed): Option<Promise<void>> {
      const cores = [...this.#cores.all()].filter((core) => !core.disposed);
      const planned = new Map<RowLocalId, { readonly row: SheetRow.Handle; readonly formula: string }>();
      for (const core of cores) {
        for (const plan of core.renamePlan(localId, renamed.from, renamed.to)) {
          if (!planned.has(plan.row.id)) planned.set(plan.row.id, plan);
        }
      }
      if (planned.size === 0) return null;
      const group = this.#deps.rowWrites.ledger.group();
      const rewrites: FormulaRewrite[] = [];
      try {
        for (const { row, formula } of planned.values()) {
          const rewrite = beginFormulaRewrite(row, formula, renamed.actor);
          rewrites.push(rewrite);
          group.add(rewrite.write);
        }
      } catch (err) { // after-failed-write: only local rewrite setup ran; no transport was sent. FormulaRewrite.send owns recovery after a sent failure.
        rollbackRename(rewrites.map(rewrite => () => rewrite.takeBack(err)),
          cores.map(core => () => { if (!core.disposed) core.rebuildGraph(); }));
        throw err;
      }
      return renamed.landed.then(
        async () => {
          const sent = await Promise.allSettled(rewrites.map((rewrite) => rewrite.send(renamed.mode)));
          // The SDK display runs before the rows-side label display: project
          // the settled model after accepted rewrites and individual take-backs.
          for (const core of cores) if (!core.disposed) core.rebuildGraph();
          const refused = sent.find((result) => result.status === "rejected");
          if (refused != null && refused.status === "rejected") throw refused.reason;
        },
        (err) => {
          // The label was refused: its rewrites go back with it, one group.
          // The label went back first: re-derive each graph with the old
          // formulas, so the old label's references resolve again.
          rollbackRename(rewrites.map(rewrite => () => rewrite.takeBack(err)),
            cores.map(core => () => { if (!core.disposed) core.rebuildGraph(); }));
        },
      );
    }

    /**
     * After a promote into sheet `created.sheet` that may have stored: every
     * open handle of that sheet re-reads its row list and lists the row the
     * promote created (`recoverCreatedRow`). A re-read that fails is traced;
     * the promote rejected with its own error either way.
     */
    #recoverPromoted(created: RowMirror.MayHaveCreated): void {
      const sheet = created.sheet.toString();
      for (const handle of this.#cores.all()) {
        if (handle.id.toString() !== sheet) continue;
        void recoverCreatedRow(handle.rows, { type: created.type, lookupKey: created.lookupKey }, "row.promote", RowAccessor.ACTOR).catch((err: unknown) =>
          Tracing.warn("net")(`row.promote: the re-read after a promote that may have stored failed: ${String(err)}`)
        );
      }
    }

    /**
     * After a row was linked into sheet `sheetId` (`client.rows.linkRow`,
     * landed or unanswered): every open handle of that sheet re-reads its
     * row list and lists the top-level row `lookupKey` names
     * (`recoverCreatedRow`), its insert announced as `actor`. Rejects with
     * the first re-read that failed.
     */
    async #listLinked(sheetId: SheetId, lookupKey: RowKey.Keyed, actor: Actor): Promise<void> {
      const sheet = sheetId.toString();
      const match = { type: null, lookupKey: RowNetworkKey.of(lookupKey) };
      const reads: Promise<void>[] = [];
      for (const handle of this.#cores.all()) {
        if (handle.id.toString() !== sheet) continue;
        reads.push(recoverCreatedRow(handle.rows, match, "rows.linkRow", actor));
      }
      await Promise.all(reads);
    }

    /**
     * Re-register the row `localId` under its lookup key `to` (the key
     * `from` released): `client.rows.open(key)` resolves it from the
     * registry, and every open handle's lookup maps are re-derived, so
     * `handle.rows.peek({ lookupKey })` finds it.
     */
    #mirrorLookupKey(localId: RowLocalId, from: Option<string>, to: Option<string>): void {
      if (from != null) this.#deps.ids.release(RowKey.of(from), localId);
      if (to != null) this.#deps.ids.alias(RowKey.of(to), localId);
      for (const handle of this.#cores.opened.values()) {
        handle.platformView().local.update(localId, { lookupKey: Patch.fromWire(to) }, SheetHandle.ACTOR);
      }
    }
  }

  export namespace Client {
    /**
     * A verb of a disposed client (`client.dispose()`, or the end of its
     * `using` block): a client verb, a facade (`client.sheets`, `rows`,
     * `events`, `calendars`, `tasks`, `preferences`, `user`, `chats`,
     * `themes`) or a verb of one taken before, a row opened through
     * `client.rows.open`, a `platformOf(client)` writer or a durable write.
     * Build a new client. `verb` names what was attempted.
     */
    export const DisposedError: typeof ClientDisposedError = ClientDisposedError;
    /** See the value `LonaSdk.Client.DisposedError`. */
    export type DisposedError = ClientDisposedError;

    /**
     * The liveness a client shares with its facades: each facade's
     * constructor takes one, and each network verb calls
     * `assertLive(verb)` first. A client passes its own (retired by its
     * `dispose`); a facade built without a client — a test, a public page's
     * scheduling-links store — takes `new LonaSdk.Client.Lifecycle()`.
     */
    export const Lifecycle: typeof ClientLifecycle = ClientLifecycle;
    /** See the value `LonaSdk.Client.Lifecycle`. */
    export type Lifecycle = ClientLifecycle;

    /**
     * Host-owned rows-side seams, returned by `ClientConfig.rowsOptions`. Every
     * field is required; `null` only where noted.
     */
    export interface RowsOptions {
      /** Rows-side backend: `client.httpBackend()` live, or an offline `MemoryRowsBackend`. */
      readonly backend: Backend;
      /** Feeds the `calendar-list` alias's dynamic tail. */
      readonly calendarConfig: CalendarConfigProvider;
      /** Feeds the `task-list` alias's dynamic tail. */
      readonly taskListConfig: TaskListConfigProvider;
      /**
       * Preference-driven aliases (`alias.secondary-timezones`,
       * `alias.default-weather`). The DOM app passes
       * `client.#preferences.calendar()`. `null` registers empty stubs for both
       * templates so sheets that reference them still hydrate (offline tests).
       */
      readonly calendarPrefs: Option<CalendarPreferences>;
    }

    /**
     * A client's local caches, for a render back-end or a host bootstrap
     * (`platformOf(client)`, `@tento/lona/host`). LOCAL ONLY: no
     * request, no verdict.
     */
    /**
     * `client.txLog`: the rows-side transaction log's reads, no recorder — a
     * frozen object forwarding to the log (never the log itself), whose
     * `transactions()` answers a frozen copy of the retained window.
     */
    export interface TxLogReads {
      /** The read mark τ: the `txId` of the last sealed transaction (0 before any seal). */
      readonly tau: () => number;
      /** The retained sealed transactions, in seal order (a frozen copy). */
      readonly transactions: () => ReturnType<SheetModelCache["txLog"]["transactions"]>;
      /** The all-time counters. */
      readonly counters: () => ReturnType<SheetModelCache["txLog"]["counters"]>;
    }

    /**
     * `platformOf(client).canonicalCells`: the canonical cell store's reads
     * and change subscriptions — no writer. The full store is a white-box
     * door (`clientInstrumentationOf(client).canonicalCells`,
     * `@tento-lona/internal-testing`).
     */
    export type CanonicalCellReads = Pick<
      SheetModelCache["canonicalCells"],
      "get" | "cells" | "customCells" | "has" | "isStale" | "subscribe" | "subscribeAll"
    >;

    /**
     * The client's local-edit chokepoint (`platformOf(client).local`): cell
     * paints that reach every surface rendering a row — rows-side facades,
     * shared RowHandles, legends, each open sheet that carries it — without a
     * refetch. No network I/O; the caller owns persistence.
     */
    export interface Local {
      /**
       * Paint a cell edit into the cell of `row` that `target` addresses.
       * Addressed as `platformOf(handle).local.writeCell` is: `row` is a
       * `RowLocalId`, a `RowId` (`RowId.of(":~key")` for a lookup-keyed
       * row, whose cells land in the canonical cell store) or a held row;
       * `target` is the column's date-keyed cell (`CellTarget.date`) or an
       * id-keyed one of a multi-cardinality row (`CellTarget.addWithId(date,
       * id)`, `CellTarget.keyed`, `CellTarget.cell`). Nothing checks the
       * row's cardinality here (the row may be one no sheet holds): address
       * a single-cardinality row's cell by its column.
       * `actor` is the writing surface: it stamps the cell and the
       * invalidations, so the surface can skip its own echo.
       */
      writeCell(
        row: SheetHandle.RowRef,
        target: CellTarget.DateKeyed,
        data: Json | CellValue,
        actor: Actor,
      ): void;
      /**
       * Clear a cell through the same chokepoint: every surface rendering
       * `row` drops the cell `target` addresses. `actor` as for `writeCell`.
       */
      clearCell(row: SheetHandle.RowRef, target: CellTarget.DateKeyed, actor: Actor): void;
    }

    /** The host's theme seam (`platformOf(client).themes`). */
    export interface Themes {
      /**
       * Seed `client.themes` with the theme the server holds (the initial
       * payload's), silently: `client.themes.live` holds it and the host's
       * theme backend applies it; nothing is stored, sent or announced. It is
       * what a refused `client.themes.setDurable` takes the choice back to.
       * A host seeds at boot and on a reload of the user's payload; an app
       * chooses a theme with `client.themes.setDurable(theme, actor)`.
       */
      seed(theme: Option<ThemesClient.Theme>): void;
    }

    export interface Platform {
      /**
       * The client's `RowLocalId` registry — its identity scope — resolve-only
       * (`RowLocalId.Resolver`: no alias / claim / rekey / setCurrentUser).
       * Every sheet, row and rows-side accessor this client builds interns
       * through it: `platformOf(client).ids.resolve(RowKey.of(key))`.
       */
      readonly ids: RowLocalId.Resolver;
      /**
       * The page this client runs in, watched for its unload
       * (`PageLifecycle`): a host's own request loads report through
       * `page.reportUnlessLeaving`, as the client's cell fetches do, so an
       * abort the page's unload caused is recorded at info, not as an error.
       */
      readonly page: PageLifecycle;
      /**
       * The client-scoped canonical cell store — the one home of every `:~`
       * lookup-keyed row's cells, shared by every sheet that shows it — as
       * its reads (`CanonicalCellReads`). A host paints a local cell through
       * `local.writeCell` (the one chokepoint, which routes a `:~` key
       * here and notifies every surface showing it).
       */
      readonly canonicalCells: CanonicalCellReads;
      /**
       * Open a sheet rows-side only — no `SheetHandle` — through the backend
       * `ClientConfig.rowsOptions` supplied (an offline `MemoryRowsBackend`
       * included), which `client.open` never consults (`Backend.fetchSheet`
       * via the client's accessor; an open sheet is returned as is), and hand
       * out its reads (`SheetModelReads`). For a render back-end or a CLI
       * reader; an app opens `client.open(...)`.
       */
      openSheet(key: SheetKey | SheetId): Promise<SheetModelReads>;
      /**
       * Hydrate `spec` as an ephemeral rows-side sheet through the same
       * accessor, locally — no round-trip, no `SheetHandle` — and hand out
       * its reads. Throws when a sheet with its id is already open.
       */
      ephemeralSheet(spec: LonaSdk.EphemeralSheet): SheetModelReads;
      /**
       * The reads of the rows-side sheet this client hydrated under `key`
       * (`openSheet`, `ephemeralSheet`, or an open `SheetHandle`'s model), or
       * `null` when none is open. Reads nothing.
       */
      peekSheet(key: SheetKey | SheetId): Option<SheetModelReads>;
      /**
       * Register a row created outside any `SheetHandle` (a row the host
       * just POSTed) under `localId`, so the render tree and
       * `rowRegistry` resolve it. `RowTypeDescriptor` overrides apply on
       * registration. Returns the registered row's reads face
       * (`SheetRowReads`, what `client.rowRegistry.peek(localId)` answers).
       */
      registerRow(localId: RowLocalId, row: SheetRow.Input): SheetRowReads;
      /**
       * The local-edit chokepoint (`LonaSdk.Client.Local`): paint or clear a
       * cell of any row, so every surface rendering it updates without a
       * refetch. No network I/O; the caller owns persistence.
       */
      readonly local: Local;
      /** The host's theme seam (`LonaSdk.Client.Themes`): the boot seed. */
      readonly themes: Themes;
      /**
       * Install host descriptor overrides (keyed by wire row type — layout
       * spec, cell layout options, label builder) and re-apply every override
       * to the rows already registered. Rows registered later get them on
       * registration. Passing no overrides re-applies the installed ones.
       */
      registerDescriptorOverrides(overrides: Iterable<[string, RowTypeDescriptor.Override]>): void;
      /**
       * Apply the locally-stored row-height preferences to every registered
       * row. `open` / `parseSheet` already apply them to the rows they
       * register.
       */
      applyStoredRowHeights(): void;
      /**
       * Evict a sheet's live handle (every cache alias) and its rows-side
       * cache, so the next open re-seeds it — what `client.#sheets.delete`
       * does for the sheet it deleted. LOCAL ONLY: nothing is sent.
       */
      closeSheet(sheetId: SheetId): void;
      /** Announce that `column`'s data changed on every row
       *  (`ColumnDataChanged(column, null)` on the client's sink, as
       *  `SheetDataSource.ACTOR`). LOCAL ONLY: nothing is fetched. */
      invalidateColumn(column: NaiveDate.Partial): void;
      /**
       * Seed the client's caches with what a boot payload already carries
       * (`LonaSdk.Client.Boot`): the signed-in user's calendar lists — the
       * load `client.calendars.list()` would run — and the viewer's sheet
       * list, so `client.calendars.peekAll()` and `client.sheets.peek` /
       * `peekAll` answer before any request. LOCAL ONLY: nothing is sent
       * and nothing is announced (the lists are the server's own reads, not
       * edits). Once per client, before the calendars load: a second seed,
       * or one after `client.calendars` loaded, throws — it would otherwise
       * be a silent no-op.
       */
      seedBoot(boot: Boot): void;
    }

    /** What a boot payload carries for `platformOf(client).seedBoot`. */
    export type Boot = {
      /** The signed-in user the calendar lists are for. */
      readonly user: UserId;
      /** The user's calendar lists, as `GET /api/calendars` answers them. */
      readonly calendars: readonly Network.CalendarList[];
      /** The viewer's sheet list, as `GET /api/sheets` answers it. */
      readonly sheets: readonly Network.UserSheet[];
    };

    /** Provider with no calendars: the `calendar-list` tail expands to nothing. */
    export const EMPTY_CALENDAR_CONFIG: CalendarConfigProvider = deepFrozen({
      calendars: () => [],
    });

    /** Provider with no task lists: only the static `:~lona:tasks` default survives. */
    export const EMPTY_TASK_LIST_CONFIG: TaskListConfigProvider = deepFrozen({
      taskLists: () => [],
    });

    /** Where a `headlessScript` client connects. Both are required: the SDK
     *  has no default server. */
    export type HeadlessScriptTarget = {
      /** API origin, e.g. `https://lona.so`. */
      readonly baseUrl: string;
      /** Bearer token. */
      readonly accessToken: string;
    };

    /**
     * The `ClientConfig` of a script (a CLI, a server-side job, an example)
     * talking to `target`, with every decision the name implies stated:
     *
     * - `debounceExecutor: DebounceExecutors.inProcess()` — durable writes
     *   run in this process and report their own refusals;
     * - `requestErrorHandler: RequestErrorHandlers.THROW` — a failed
     *   tolerant read throws instead of reading empty;
     * - `host: SheetHost.HEADLESS`, `registries: SheetRegistries.BUILTINS`,
     *   `storage: new MemoryStorage()`,
     *   `invalidation: InvalidationSink.create()`, `fetch: null` (the
     *   global `fetch`), `onSheetPromoted: null`,
     *   `appearance: Appearance.HEADLESS` (no theme, light);
     * - `rows`: the client's own HTTP backend (`client.httpBackend()`) with
     *   `EMPTY_CALENDAR_CONFIG`, `EMPTY_TASK_LIST_CONFIG` and no calendar
     *   preferences.
     *
     * A plain value: override any field by spreading it
     * (`{ ...LonaSdk.Client.headlessScript(target), fetch: recording }`).
     * Each call returns fresh storage and a fresh sink.
     *
     * ```ts
     * using client = new LonaSdk.Client(
     *   LonaSdk.Client.headlessScript({ baseUrl, accessToken }),
     * );
     * ```
     */
    export function headlessScript(target: HeadlessScriptTarget): ClientConfig {
      return {
        storage: new MemoryStorage(),
        host: SheetHost.HEADLESS,
        registries: SheetRegistries.BUILTINS,
        invalidation: InvalidationSink.create(),
        onSheetPromoted: null,
        baseUrl: target.baseUrl,
        accessToken: target.accessToken,
        fetch: null,
        rowsOptions: (client) => ({
          backend: client.httpBackend(),
          calendarConfig: EMPTY_CALENDAR_CONFIG,
          taskListConfig: EMPTY_TASK_LIST_CONFIG,
          calendarPrefs: null,
        }),
        requestErrorHandler: RequestErrorHandlers.THROW,
        debounceExecutor: DebounceExecutors.inProcess(),
        appearance: Appearance.HEADLESS,
      };
    }
  }
}
